/**
 * PublicSitePage — portal layout inspired by trolybacsi.vn.
 * Structure: Nav → Hero+Search → Specialty grid → Doctors → Services →
 *            Stats bar → Why us → Booking steps → Branches → Footer.
 * Data comes entirely from /v1/public/site/:slug — no section config used.
 */
import { Children, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  Phone, X, Search, Calendar, Stethoscope, Building2,
  MapPin, Clock, CheckCircle2, Heart, Menu, ChevronRight, ChevronLeft,
  Star, ClipboardList, BadgeCheck, Microscope, Brain, Bone, Eye,
  Baby, Pill, Smile, Ear, FlaskConical, Syringe, Activity,
  ArrowRight, ChevronDown, HelpCircle, ShieldCheck, Package, ScanLine, UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { createDemoBookingProvider, type BookingProvider, type BookingIntent } from "./booking-provider";

// ─── Types ────────────────────────────────────────────────────────────────────

type SitePayload = {
  slug: string;
  clinicName: string;
  themeId: string;
  publishedAt: string | null;
  configuration: {
    themeId: string;
    pages: { type: string; slug: string; title: string; sections: unknown[] }[];
  };
  data: {
    services?:    { id: string; name: string; description?: string; category?: string; priceVnd?: number | null }[];
    doctors?:     { id: string; name: string; title?: string; specialty?: string; bio?: string }[];
    specialties?: { id: string; name: string }[];
    branches?:    { id: string; name: string; address?: string; phone?: string }[];
  };
};

// ─── Palettes (hardcoded full strings for Tailwind JIT) ──────────────────────

const SP_COLORS = [
  { bg: "bg-sky-100",     text: "text-sky-600",     icon: Stethoscope  },
  { bg: "bg-rose-100",    text: "text-rose-600",     icon: Heart        },
  { bg: "bg-violet-100",  text: "text-violet-600",   icon: Brain        },
  { bg: "bg-emerald-100", text: "text-emerald-600",  icon: Microscope   },
  { bg: "bg-amber-100",   text: "text-amber-600",    icon: Pill         },
  { bg: "bg-teal-100",    text: "text-teal-600",     icon: FlaskConical },
  { bg: "bg-orange-100",  text: "text-orange-600",   icon: Syringe      },
  { bg: "bg-indigo-100",  text: "text-indigo-600",   icon: Eye          },
  { bg: "bg-pink-100",    text: "text-pink-600",     icon: Baby         },
  { bg: "bg-cyan-100",    text: "text-cyan-600",     icon: Ear          },
  { bg: "bg-lime-100",    text: "text-lime-600",     icon: Bone         },
  { bg: "bg-fuchsia-100", text: "text-fuchsia-600",  icon: Smile        },
] as const;

// Free-to-use doctor portrait photos (Pexels License — free for commercial
// use, no attribution required), cycled across doctor cards.
const DOCTOR_PHOTOS = [
  "https://images.pexels.com/photos/8376309/pexels-photo-8376309.jpeg?cs=srgb&fm=jpg&w=320",
  "https://images.pexels.com/photos/15752232/pexels-photo-15752232.jpeg?cs=srgb&fm=jpg&w=320",
  "https://images.pexels.com/photos/4989179/pexels-photo-4989179.jpeg?cs=srgb&fm=jpg&w=320",
  "https://images.pexels.com/photos/6129500/pexels-photo-6129500.jpeg?cs=srgb&fm=jpg&w=320",
  "https://images.pexels.com/photos/19218034/pexels-photo-19218034.jpeg?cs=srgb&fm=jpg&w=320",
] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtPrice(v: number | null | undefined) {
  if (v == null) return "Liên hệ";
  if (v === 0)   return "Miễn phí";
  return new Intl.NumberFormat("vi-VN").format(v) + " đ";
}

function jump(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

const publicClient = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api",
});

// ─── Carousel row ───────────────────────────────────────────────────────────
// Always a horizontal scroller (mobile AND desktop) — only a few cards show at
// once, like TroLyBacSi's rows, instead of unwrapping into a full static grid.

// A card cut off hard at the scroll edge reads as broken, not "scroll for
// more" — fade it out instead, and only on whichever side actually still has
// content (a fully-scrolled-to-the-end row must not fade its genuinely last
// card).
function edgeMask(canLeft: boolean, canRight: boolean): string {
  const left = canLeft ? "transparent 0, black 24px" : "black 0";
  const right = canRight ? "black calc(100% - 24px), transparent 100%" : "black 100%";
  return `linear-gradient(to right, ${left}, ${right})`;
}

function CarouselRow({ children, dots = false }: { children: ReactNode; dots?: boolean }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dotCount = Children.count(children);
  const [active, setActive] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  function updateEdges() {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < max - 4);
  }

  useEffect(() => {
    updateEdges();
    window.addEventListener("resize", updateEdges);
    return () => window.removeEventListener("resize", updateEdges);
  }, [dotCount]);

  function scrollByCards(dir: 1 | -1) {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  }

  function scrollToDot(i: number) {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    el.scrollTo({ left: max <= 0 ? 0 : (i / Math.max(dotCount - 1, 1)) * max, behavior: "smooth" });
  }

  function handleScroll() {
    updateEdges();
    if (!dots) return;
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const ratio = max <= 0 ? 0 : el.scrollLeft / max;
    setActive(Math.round(ratio * Math.max(dotCount - 1, 0)));
  }

  return (
    <div className="relative sm:px-9">
      <div
        ref={trackRef}
        onScroll={handleScroll}
        style={{
          WebkitMaskImage: edgeMask(canScrollLeft, canScrollRight),
          maskImage: edgeMask(canScrollLeft, canScrollRight),
        }}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {children}
      </div>
      {/* Arrows sit in the side gutter (sm:px-9 above) — never over card content.
          Hidden entirely when there's nothing in that direction to scroll to —
          a row that already fits shouldn't show a button that does nothing. */}
      {canScrollLeft && (
        <button
          type="button"
          aria-label="Trước"
          onClick={() => scrollByCards(-1)}
          className="absolute left-0 top-1/2 hidden size-8 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md hover:bg-primary/90 sm:flex"
        >
          <ChevronLeft className="size-4" />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          aria-label="Tiếp"
          onClick={() => scrollByCards(1)}
          className="absolute right-0 top-1/2 hidden size-8 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md hover:bg-primary/90 sm:flex"
        >
          <ChevronRight className="size-4" />
        </button>
      )}

      {dots && dotCount > 1 && (
        <div className="mt-2 flex items-center justify-center gap-1.5">
          {Array.from({ length: dotCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Mục ${i + 1}`}
              onClick={() => scrollToDot(i)}
              className={cn(
                "h-1.5 rounded-full transition-all",
                i === active ? "w-5 bg-primary" : "w-1.5 bg-border hover:bg-muted-foreground/40",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Nav ──────────────────────────────────────────────────────────────────────

function SiteNav({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const [open, setOpen] = useState(false);
  const phone = payload.data.branches?.[0]?.phone;
  const NAV = [
    { label: "Chuyên khoa", id: "sec-specialties" },
    { label: "Bác sĩ",     id: "sec-doctors"     },
    { label: "Dịch vụ",    id: "sec-services"    },
    { label: "Liên hệ",    id: "sec-contact"     },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-card/95 shadow-sm backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center gap-6 px-4 py-3.5 sm:px-6">
        <a href={`/site/${payload.slug}`} className="flex shrink-0 items-center gap-2.5 font-bold text-foreground">
          <span className="flex size-9 items-center justify-center rounded-xl bg-primary text-sm font-bold text-primary-foreground shadow-sm">
            {payload.clinicName.charAt(0).toUpperCase()}
          </span>
          <span className="hidden max-w-[220px] truncate text-lg font-bold sm:block">{payload.clinicName}</span>
        </a>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => jump(n.id)}
              className="rounded-full px-3.5 py-2 text-[15px] font-medium text-foreground/80 transition-colors hover:bg-primary/10 hover:text-primary">
              {n.label}
            </button>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <Button onClick={() => booking.openBooking()} className="rounded-full shadow-sm">
            Đặt lịch khám
          </Button>
          <button className="rounded-lg p-2.5 text-muted-foreground hover:bg-muted lg:hidden"
            onClick={() => setOpen((v) => !v)} aria-label="Menu">
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-card px-4 pb-4 lg:hidden">
          <nav className="flex flex-col gap-1 pt-3">
            {NAV.map((n) => (
              <button key={n.id}
                onClick={() => { jump(n.id); setOpen(false); }}
                className="rounded-lg px-4 py-3 text-left text-sm font-semibold text-foreground hover:bg-muted">
                {n.label}
              </button>
            ))}
            {phone && (
              <a href={`tel:${phone}`}
                className="flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-primary">
                <Phone className="size-4" />{phone}
              </a>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

// ─── Doctors ──────────────────────────────────────────────────────────────────

function DoctorCard({ doc, idx, booking }: {
  doc: NonNullable<SitePayload["data"]["doctors"]>[number];
  idx: number;
  booking: BookingProvider;
}) {
  const photo = DOCTOR_PHOTOS[idx % DOCTOR_PHOTOS.length];
  return (
    <Card className="flex h-full flex-col overflow-hidden transition-all hover:-translate-y-1 hover:shadow-lg">
      <CardContent className="flex flex-1 flex-col items-center gap-2 p-4 text-center">
        {/* Photo */}
        <div className="relative">
          <img src={photo} alt="" className="size-20 rounded-full object-cover shadow-sm" />
          <span className="absolute -bottom-1 -right-1 flex size-6 items-center justify-center rounded-full border-2 border-card bg-green-500">
            <BadgeCheck className="size-3.5 text-white" />
          </span>
        </div>

        {/* Name + credentials */}
        <div>
          <p className="text-[11px] text-muted-foreground">{doc.title ?? "Bác sĩ"}</p>
          <h3 className="font-bold text-foreground leading-snug">{doc.name}</h3>
          {doc.specialty && (
            <span className="mt-1 inline-block rounded bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
              {doc.specialty}
            </span>
          )}
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1.5">
          <div className="flex">
            {[1,2,3,4,5].map((n) => (
              <Star key={n} className="size-3.5 fill-amber-400 text-amber-400" />
            ))}
          </div>
          <span className="text-xs text-muted-foreground">5.0 · 100+ lượt đặt</span>
        </div>

        {/* Bio */}
        {doc.bio && (
          <p className="text-xs leading-relaxed text-muted-foreground line-clamp-2">{doc.bio}</p>
        )}

        <Button
          size="sm"
          className="mt-auto w-full rounded-full"
          onClick={() => booking.bookDoctor(doc.id)}
        >
          Đặt lịch khám
        </Button>
      </CardContent>
    </Card>
  );
}


// ─── Services ─────────────────────────────────────────────────────────────────

// Category badge colour + icon, overlaid on a real photo (Pexels License — free
// for commercial use, no attribution required). Cycles independently of the
// photo list so colour and photo don't always repeat together.
const SVC_THEMES = [
  { icon: Stethoscope,   badge: "bg-sky-500"     },
  { icon: Heart,         badge: "bg-rose-500"    },
  { icon: Microscope,    badge: "bg-violet-500"  },
  { icon: FlaskConical,  badge: "bg-emerald-500" },
  { icon: Syringe,       badge: "bg-amber-500"   },
  { icon: Activity,      badge: "bg-teal-500"    },
  { icon: ClipboardList, badge: "bg-orange-500"  },
  { icon: Eye,           badge: "bg-indigo-500"  },
] as const;

// Free-to-use stock photos (Pexels License) picked per general medical topic —
// general checkup, diagnostic imaging, lab test, vaccination, cardiology, consult.
const SVC_PHOTOS = [
  "https://images.pexels.com/photos/40568/medical-appointment-doctor-healthcare-40568.jpeg?cs=srgb&fm=jpg&w=480",
  "https://images.pexels.com/photos/7108421/pexels-photo-7108421.jpeg?cs=srgb&fm=jpg&w=480",
  "https://images.pexels.com/photos/2280549/pexels-photo-2280549.jpeg?cs=srgb&fm=jpg&w=480",
  "https://images.pexels.com/photos/5922094/pexels-photo-5922094.jpeg?cs=srgb&fm=jpg&w=480",
  "https://images.pexels.com/photos/28585011/pexels-photo-28585011.jpeg?cs=srgb&fm=jpg&w=480",
  "https://images.pexels.com/photos/7659873/pexels-photo-7659873.jpeg?cs=srgb&fm=jpg&w=480",
] as const;

// ─── Theme-specific demo data ─────────────────────────────────────────────────
// Each theme shows content appropriate for its clinic type when the real tenant
// data is empty or doesn't match the theme's focus area.
type DemoSpecialty = { id: string; name: string };
type DemoService   = { id: string; name: string; description?: string; category?: string; priceVnd?: number | null };

const PREMIUM_DEMO_SPECIALTIES: DemoSpecialty[] = [
  { id: "pm1", name: "Thụ tinh ống nghiệm (IVF)" },
  { id: "pm2", name: "Tầm soát ung thư toàn diện" },
  { id: "pm3", name: "Khám tổng quát VIP" },
  { id: "pm4", name: "Tim mạch chuyên sâu" },
  { id: "pm5", name: "Y học thể thao" },
  { id: "pm6", name: "Dinh dưỡng lâm sàng" },
];
const PREMIUM_DEMO_SERVICES: DemoService[] = [
  { id: "pms1", name: "Gói khám tổng quát VIP", description: "35+ hạng mục, kết quả trong ngày", priceVnd: 5500000 },
  { id: "pms2", name: "Tầm soát ung thư tiêu hóa", description: "Nội soi dạ dày + đại tràng + marker", priceVnd: 8500000 },
  { id: "pms3", name: "Thụ tinh trong ống nghiệm (IVF)", description: "Tư vấn & thực hiện bởi chuyên gia", priceVnd: null },
  { id: "pms4", name: "Chụp MRI toàn thân", description: "Phát hiện sớm bất thường nội tạng", priceVnd: 12000000 },
  { id: "pms5", name: "Gói sức khoẻ doanh nhân", description: "Khám 2h, báo cáo chi tiết, tái khám 1 năm", priceVnd: 7000000 },
  { id: "pms6", name: "Khám tim mạch toàn diện", description: "Siêu âm tim, CT mạch vành, tư vấn chuyên gia", priceVnd: 3500000 },
];

const SPECIALIST_DEMO_SPECIALTIES: DemoSpecialty[] = [
  { id: "sp1", name: "Tim mạch" },
  { id: "sp2", name: "Cơ xương khớp" },
  { id: "sp3", name: "Thần kinh" },
  { id: "sp4", name: "Tiêu hóa" },
  { id: "sp5", name: "Nội tiết & Đái tháo đường" },
  { id: "sp6", name: "Hô hấp" },
  { id: "sp7", name: "Thận - Tiết niệu" },
];
const SPECIALIST_DEMO_SERVICES: DemoService[] = [
  { id: "sps1",  name: "Khám tim mạch chuyên sâu",         description: "Siêu âm tim, điện tim, tư vấn chuyên gia tim mạch",       priceVnd: 500000, category: "Tim mạch" },
  { id: "sps2",  name: "Siêu âm tim màu",                   description: "Đánh giá chức năng tim, van tim, buồng tim",              priceVnd: 450000, category: "Tim mạch" },
  { id: "sps3",  name: "Khám cơ xương khớp",                description: "Đau lưng, thoát vị đĩa đệm, viêm khớp dạng thấp",         priceVnd: 350000, category: "Cơ xương khớp" },
  { id: "sps4",  name: "Vật lý trị liệu",                   description: "Phục hồi chức năng sau chấn thương, thoái hóa khớp",      priceVnd: 300000, category: "Cơ xương khớp" },
  { id: "sps5",  name: "Khám thần kinh",                    description: "Đau đầu mãn tính, mất ngủ, rối loạn tiền đình",           priceVnd: 350000, category: "Thần kinh" },
  { id: "sps6",  name: "Điện não đồ (EEG)",                 description: "Chẩn đoán động kinh, rối loạn giấc ngủ",                  priceVnd: 400000, category: "Thần kinh" },
  { id: "sps7",  name: "Khám tiêu hóa",                     description: "Đau dạ dày, viêm đại tràng, trào ngược dạ dày",           priceVnd: 300000, category: "Tiêu hóa" },
  { id: "sps8",  name: "Nội soi dạ dày",                    description: "Tầm soát viêm loét, polyp, ung thư dạ dày",              priceVnd: 800000, category: "Tiêu hóa" },
  { id: "sps9",  name: "Khám nội tiết - Đái tháo đường",    description: "Kiểm soát đường huyết, tuyến giáp, béo phì",             priceVnd: 400000, category: "Nội tiết & Đái tháo đường" },
  { id: "sps10", name: "Xét nghiệm HbA1c",                  description: "Đánh giá kiểm soát đường huyết 3 tháng gần nhất",         priceVnd: 150000, category: "Nội tiết & Đái tháo đường" },
  { id: "sps11", name: "Khám hô hấp",                       description: "Hen suyễn, COPD, viêm phổi, khó thở mãn tính",           priceVnd: 300000, category: "Hô hấp" },
  { id: "sps12", name: "Đo chức năng hô hấp",                description: "Đánh giá dung tích phổi, phát hiện sớm COPD",             priceVnd: 250000, category: "Hô hấp" },
  { id: "sps13", name: "Khám thận - tiết niệu",             description: "Sỏi thận, nhiễm trùng tiết niệu, suy thận",              priceVnd: 350000, category: "Thận - Tiết niệu" },
  { id: "sps14", name: "Siêu âm thận - tiết niệu",          description: "Tầm soát sỏi, u nang, bất thường cấu trúc thận",         priceVnd: 300000, category: "Thận - Tiết niệu" },
];

const FAMILY_DEMO_SPECIALTIES: DemoSpecialty[] = [
  { id: "fc1", name: "Khám Nhi tổng quát" },
  { id: "fc2", name: "Sản - Phụ khoa" },
  { id: "fc3", name: "Tiêm chủng trẻ em" },
  { id: "fc4", name: "Khám tổng quát gia đình" },
  { id: "fc5", name: "Dinh dưỡng & tăng trưởng" },
  { id: "fc6", name: "Theo dõi sức khoẻ mãn tính" },
];
const FAMILY_DEMO_SERVICES: DemoService[] = [
  { id: "fcs1",  name: "Khám nhi tổng quát",          description: "Trẻ sơ sinh đến 15 tuổi",                priceVnd: 200000, category: "Khám Nhi tổng quát" },
  { id: "fcs2",  name: "Tiêm vaccine trẻ em",         description: "Lịch tiêm chuẩn Bộ Y tế",                priceVnd: null,   category: "Tiêm chủng trẻ em" },
  { id: "fcs3",  name: "Tư vấn dinh dưỡng trẻ",       description: "Phát triển toàn diện từ nhỏ",            priceVnd: 150000, category: "Dinh dưỡng & tăng trưởng" },
  { id: "fcs4",  name: "Khám thai định kỳ",           description: "Theo dõi thai kỳ từ tuần 6–40",          priceVnd: 250000, category: "Sản - Phụ khoa" },
  { id: "fcs5",  name: "Siêu âm thai nhi 4D",         description: "Máy siêu âm 4D hiện đại",                priceVnd: 350000, category: "Sản - Phụ khoa" },
  { id: "fcs6",  name: "Khám phụ khoa định kỳ",       description: "Tầm soát sớm, kín đáo, riêng tư",        priceVnd: 300000, category: "Sản - Phụ khoa" },
  { id: "fcs7",  name: "Khám sức khoẻ gia đình",      description: "Gói tổng quát cho cả nhà",               priceVnd: 800000, category: "Khám tổng quát gia đình" },
  { id: "fcs8",  name: "Khám sức khoẻ định kỳ",       description: "Xét nghiệm máu, nước tiểu, X-quang",     priceVnd: 450000, category: "Khám tổng quát gia đình" },
  { id: "fcs9",  name: "Tư vấn dinh dưỡng người lớn", description: "Điều chỉnh chế độ ăn theo bệnh nền",      priceVnd: 200000, category: "Dinh dưỡng & tăng trưởng" },
  { id: "fcs10", name: "Tiêm vaccine người lớn",      description: "Cúm mùa, viêm gan, uốn ván",             priceVnd: null,   category: "Tiêm chủng trẻ em" },
  { id: "fcs11", name: "Theo dõi huyết áp, tiểu đường", description: "Tái khám định kỳ, điều chỉnh đơn thuốc", priceVnd: 180000, category: "Theo dõi sức khoẻ mãn tính" },
  { id: "fcs12", name: "Theo dõi bệnh tim mạch mạn tính", description: "Tái khám định kỳ theo phác đồ",       priceVnd: 220000, category: "Theo dõi sức khoẻ mãn tính" },
  { id: "fcs13", name: "Khám tăng trưởng chiều cao, cân nặng", description: "Trẻ 0–15 tuổi, có biểu đồ theo dõi", priceVnd: 150000, category: "Dinh dưỡng & tăng trưởng" },
  { id: "fcs14", name: "Xét nghiệm sàng lọc trước sinh", description: "NIPT, Double test, Triple test",       priceVnd: 1200000, category: "Sản - Phụ khoa" },
];

const DIAGNOSTIC_DEMO_SPECIALTIES: DemoSpecialty[] = [
  { id: "dg1", name: "Xét nghiệm máu & nước tiểu" },
  { id: "dg2", name: "Chẩn đoán hình ảnh" },
  { id: "dg3", name: "Siêu âm" },
  { id: "dg4", name: "Điện tim (ECG)" },
  { id: "dg5", name: "Nội soi tiêu hóa" },
  { id: "dg6", name: "Tầm soát ung thư" },
];
const DIAGNOSTIC_DEMO_SERVICES: DemoService[] = [
  { id: "dgs1", name: "Gói xét nghiệm cơ bản", description: "Công thức máu, đường huyết, mỡ máu", priceVnd: 350000, category: "Xét nghiệm" },
  { id: "dgs2", name: "Gói tầm soát tiểu đường", description: "HbA1c, đường huyết lúc đói, insulin", priceVnd: 550000, category: "Xét nghiệm" },
  { id: "dgs3", name: "Siêu âm bụng tổng quát", description: "Gan, mật, tụy, lách, thận", priceVnd: 280000, category: "Chẩn đoán hình ảnh" },
  { id: "dgs4", name: "Chụp X-quang ngực thẳng", description: "Tim phổi, xương sườn, cột sống", priceVnd: 150000, category: "Chẩn đoán hình ảnh" },
  { id: "dgs5", name: "Điện tim ECG 12 chuyển đạo", description: "Tầm soát bệnh tim mạch", priceVnd: 120000, category: "Tim mạch" },
  { id: "dgs6", name: "Gói khám sức khoẻ tổng quát", description: "Xét nghiệm + siêu âm + điện tim + tư vấn", priceVnd: 1200000, category: "Gói tổng quát" },
];

const DENTAL_DEMO_SPECIALTIES: DemoSpecialty[] = [
  { id: "db1", name: "Nha khoa tổng quát" },
  { id: "db2", name: "Chỉnh nha (niềng răng)" },
  { id: "db3", name: "Implant răng" },
  { id: "db4", name: "Thẩm mỹ nụ cười" },
  { id: "db5", name: "Điều trị da mặt" },
  { id: "db6", name: "Thẩm mỹ cơ thể" },
];
const DENTAL_DEMO_SERVICES: DemoService[] = [
  { id: "dbs1", name: "Niềng răng trong suốt Invisalign", description: "Chỉnh nha không mắc cài, thẩm mỹ tuyệt đối", priceVnd: 45000000 },
  { id: "dbs2", name: "Bọc răng sứ thẩm mỹ", description: "Răng sứ cao cấp, màu sắc tự nhiên", priceVnd: 5000000 },
  { id: "dbs3", name: "Cấy ghép Implant", description: "Trồng răng cố định, như răng thật", priceVnd: 25000000 },
  { id: "dbs4", name: "Tẩy trắng răng Laser", description: "Trắng sáng ngay sau 1 buổi điều trị", priceVnd: 3500000 },
  { id: "dbs5", name: "Điều trị mụn & sẹo", description: "Laser, RF, công nghệ hiện đại", priceVnd: 2500000 },
  { id: "dbs6", name: "Trẻ hoá da PRP", description: "Tái tạo da tự nhiên bằng huyết thanh", priceVnd: 4500000 },
];

// ─── CTA Banner ───────────────────────────────────────────────────────────────

function CtaBanner({ booking }: { booking: BookingProvider }) {
  return (
    <section className="relative overflow-hidden bg-primary px-4 py-8 sm:px-6">
      <div className="pointer-events-none absolute -right-16 -top-16 size-64 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -bottom-12 left-1/3 size-48 rounded-full bg-white/10" />
      <div className="relative mx-auto max-w-2xl text-center text-primary-foreground">
        <Heart className="mx-auto mb-3 size-8 opacity-80" />
        <h2 className="text-2xl font-bold sm:text-3xl">
          Sức khoẻ của bạn — ưu tiên hàng đầu
        </h2>
        <p className="mt-3 text-primary-foreground/80">
          Đặt lịch ngay hôm nay và để đội ngũ chuyên gia đồng hành cùng bạn.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button size="lg" onClick={() => booking.openBooking()}
            className="bg-white text-primary hover:bg-white/90 shadow-lg">
            <Calendar className="mr-2 size-4" />Đặt lịch khám ngay
          </Button>
          <Button size="lg" variant="outline" onClick={() => jump("sec-doctors")}
            className="border-white/30 bg-white/10 text-white hover:bg-white/20">
            Xem bác sĩ <ArrowRight className="ml-1.5 size-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}


// ─── Skeleton / Error ─────────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-card px-6 py-3">
        <div className="mx-auto flex max-w-7xl items-center gap-4">
          <Skeleton className="size-9 rounded" />
          <Skeleton className="h-5 w-40" />
          <Skeleton className="ml-auto h-8 w-24" />
        </div>
      </div>
      <div className="px-6 py-10">
        <div className="mx-auto max-w-3xl space-y-4 text-center">
          <Skeleton className="mx-auto h-5 w-36" />
          <Skeleton className="mx-auto h-10 w-3/4" />
          <Skeleton className="mx-auto mt-4 h-36 w-full rounded-2xl" />
        </div>
      </div>
      <div className="px-6 py-6">
        <div className="mx-auto max-w-7xl">
          <Skeleton className="mb-4 h-6 w-40" />
          <div className="grid grid-cols-4 gap-3 sm:grid-cols-8">
            {Array.from({length:8}).map((_,i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
        </div>
      </div>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-background px-6 text-center">
      <div className="flex size-16 items-center justify-center rounded-2xl bg-accent">
        <Building2 className="size-8 text-primary" />
      </div>
      <h1 className="text-2xl font-bold text-foreground">Không tìm thấy website</h1>
      <p className="max-w-sm text-sm text-muted-foreground">
        Phòng khám này chưa xuất bản website, hoặc đường dẫn không đúng.
      </p>
      <Button variant="outline" onClick={() => window.history.back()}>Quay lại</Button>
    </div>
  );
}

// ─── Premium Medical theme ──────────────────────────────────────────────────
// Corporate hospital — split layout, dense data, minimal whitespace, professional feel.

const PREMIUM_HERO_PHOTO =
  "https://images.pexels.com/photos/33812023/pexels-photo-33812023.jpeg?cs=srgb&fm=jpg&w=1400";
const FAMILY_HERO_PHOTO =
  "https://images.pexels.com/photos/40568/medical-appointment-doctor-healthcare-40568.jpeg?cs=srgb&fm=jpg&w=1400";
const DIAGNOSTIC_HERO_PHOTO =
  "https://images.pexels.com/photos/2280549/pexels-photo-2280549.jpeg?cs=srgb&fm=jpg&w=1400";
const SPECIALIST_HERO_PHOTO =
  "https://images.pexels.com/photos/6129507/pexels-photo-6129507.jpeg?cs=srgb&fm=jpg&w=1400";

const FACILITY_PHOTOS = [
  { url: "https://images.pexels.com/photos/33812023/pexels-photo-33812023.jpeg?cs=srgb&fm=jpg&w=500", label: "Không gian tiếp đón" },
  { url: "https://images.pexels.com/photos/7108421/pexels-photo-7108421.jpeg?cs=srgb&fm=jpg&w=500",    label: "Phòng chẩn đoán hình ảnh" },
  { url: "https://images.pexels.com/photos/2280549/pexels-photo-2280549.jpeg?cs=srgb&fm=jpg&w=500",    label: "Phòng xét nghiệm" },
  { url: "https://images.pexels.com/photos/6129507/pexels-photo-6129507.jpeg?cs=srgb&fm=jpg&w=500",    label: "Đội ngũ y bác sĩ" },
] as const;

// ─── PremiumMedicalSite sub-components ───────────────────────────────────────

function PremiumMedicalTopBar({ payload }: { payload: SitePayload }) {
  const phone = payload.data.branches?.[0]?.phone;
  return (
    <div className="bg-yellow-800 px-4 py-2 text-center text-xs text-yellow-100">
      <span className="font-medium">⭐ Phòng khám cao cấp — bác sĩ chuyên sâu, thiết bị hiện đại</span>
      {phone && (
        <span className="ml-2 hidden sm:inline">
          · Hotline:{" "}
          <a href={`tel:${phone}`} className="font-bold text-yellow-300 underline">
            {phone}
          </a>
        </span>
      )}
    </div>
  );
}

function PremiumMedicalHero({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const d = payload.data;
  const stats = [
    { count: (d.doctors?.length ?? 0) + 50,    label: "Bệnh nhân / tháng" },
    { count: d.doctors?.length ?? 0,            label: "Bác sĩ chuyên khoa" },
    { count: 5,                                  label: "Năm kinh nghiệm" },
  ].filter((s) => s.count > 0);

  const specialtyIcons = [Stethoscope, Heart, Brain, Microscope, Pill, FlaskConical, Eye, Baby];

  return (
    <section className="relative min-h-[440px] overflow-hidden px-4 py-8 sm:px-6">
      <img src={PREMIUM_HERO_PHOTO} alt="" className="absolute inset-0 size-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/40" />
      <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-8 lg:grid-cols-[55%_45%]">
        {/* Left col */}
        <div>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-yellow-700 px-3 py-1 text-xs font-semibold text-yellow-700">
            <ShieldCheck className="size-3.5" />Phòng khám cao cấp
          </div>
          <h1 className="text-3xl font-bold text-slate-900 leading-tight">
            Chăm sóc chuyên sâu,<br />trải nghiệm riêng tư
          </h1>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            Đội ngũ bác sĩ giàu kinh nghiệm, trang thiết bị hiện đại — mang đến trải nghiệm y tế cao cấp, an tâm và riêng tư.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button onClick={() => booking.openBooking()} className="rounded-full bg-yellow-700 text-white hover:bg-yellow-800">
              <Calendar className="mr-2 size-4" />Đặt lịch khám
            </Button>
            <Button variant="outline" onClick={() => jump("sec-doctors")} className="rounded-full border-yellow-700 text-yellow-700 hover:bg-yellow-50">
              Gặp bác sĩ
            </Button>
          </div>
          {stats.length > 1 && (
            <div className="mt-6 flex flex-wrap items-center gap-0 divide-x divide-slate-200">
              {stats.map((s, i) => (
                <div key={s.label} className={cn("px-4 first:pl-0", i === 0 && "pl-0")}>
                  <div className="text-xl font-bold text-yellow-700">{s.count}+</div>
                  <div className="text-xs text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right col: specialty tiles */}
        <div className="rounded-2xl bg-amber-50/90 p-6 shadow-lg backdrop-blur-sm">
          <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-amber-700">Chuyên khoa</p>
          <div className="grid grid-cols-3 gap-3">
            {(d.specialties ?? []).slice(0, 6).map((s, i) => {
              const Icon = specialtyIcons[i % specialtyIcons.length];
              return (
                <button key={s.id} onClick={() => booking.openBooking()}
                  className="flex flex-col items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-100 p-3 shadow-sm transition-shadow hover:shadow-md">
                  <span className="flex size-8 items-center justify-center rounded-lg bg-yellow-100">
                    <Icon className="size-4 text-yellow-700" />
                  </span>
                  <span className="text-center text-[10px] font-medium text-slate-700 line-clamp-2">{s.name}</span>
                </button>
              );
            })}
            {!(d.specialties?.length) && specialtyIcons.slice(0, 6).map((Icon, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-100 p-3 shadow-sm">
                <span className="flex size-8 items-center justify-center rounded-lg bg-yellow-100">
                  <Icon className="size-4 text-yellow-700" />
                </span>
                <span className="text-center text-[10px] font-medium text-slate-400">—</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PremiumMedicalDoctors({ items, booking }: {
  items: NonNullable<SitePayload["data"]["doctors"]>;
  booking: BookingProvider;
}) {
  if (!items.length) return null;
  return (
    <section id="sec-doctors" className="relative overflow-hidden px-4 py-8 sm:px-6">
      <img src={FACILITY_PHOTOS[3].url} alt="" aria-hidden={true} className="absolute inset-0 size-full object-cover" style={{ filter: "blur(8px)", opacity: 0.08, transform: "scale(1.12)" }} />
      <div className="absolute inset-0 bg-amber-50/93" />
      <div className="relative z-10 mx-auto max-w-6xl">
        <div className="mb-5 flex items-center justify-between border-b border-amber-100 pb-3">
          <h2 className="text-lg font-bold text-slate-900">Đội ngũ bác sĩ</h2>
          <button onClick={() => booking.openBooking()} className="flex items-center gap-1 text-sm font-medium text-yellow-700 hover:underline">
            Xem tất cả <ArrowRight className="size-4" />
          </button>
        </div>
        <CarouselRow>
          {items.slice(0, 10).map((doc, i) => {
            const photo = DOCTOR_PHOTOS[i % DOCTOR_PHOTOS.length];
            return (
              <div key={doc.id} className="snap-start shrink-0 w-52">
                <div className="overflow-hidden rounded-xl border border-amber-100 bg-amber-50 shadow-sm">
                  <img src={photo} alt="" className="h-40 w-full object-cover" />
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-slate-900 leading-snug">{doc.name}</h3>
                    <p className="mt-0.5 text-xs text-yellow-700">{doc.title ?? "Bác sĩ"}</p>
                    {doc.specialty && (
                      <span className="mt-1.5 inline-block rounded bg-yellow-50 px-2 py-0.5 text-[10px] font-medium text-yellow-800">
                        {doc.specialty}
                      </span>
                    )}
                    <button onClick={() => booking.bookDoctor(doc.id)}
                      className="mt-2 w-full rounded-lg border border-yellow-700 py-1.5 text-xs font-medium text-yellow-700 hover:bg-yellow-50">
                      Đặt lịch
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </CarouselRow>
      </div>
    </section>
  );
}

function PremiumMedicalServices({ items, booking }: {
  items: NonNullable<SitePayload["data"]["services"]>;
  booking: BookingProvider;
}) {
  if (!items.length) return null;
  return (
    <section id="sec-services" className="relative overflow-hidden px-4 py-8 sm:px-6">
      <img src={FACILITY_PHOTOS[1].url} alt="" aria-hidden={true} className="absolute inset-0 size-full object-cover" style={{ filter: "blur(10px)", opacity: 0.07, transform: "scale(1.12)" }} />
      <div className="absolute inset-0 bg-slate-50/90" />
      <div className="relative z-10 mx-auto max-w-6xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">Dịch vụ &amp; Giá</h2>
          <span className="rounded-full bg-yellow-100 px-3 py-0.5 text-xs font-semibold text-yellow-800">
            {items.length} dịch vụ
          </span>
        </div>
        <div className="grid gap-x-8 sm:grid-cols-3">
          {items.slice(0, 12).map((svc, i) => {
            const Icon = SVC_THEMES[i % SVC_THEMES.length].icon;
            return (
              <div key={svc.id} className="flex items-center gap-3 border-b border-slate-100 py-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-yellow-100">
                  <Icon className="size-3.5 text-yellow-700" />
                </span>
                <span className="flex-1 text-sm font-medium text-slate-800 line-clamp-1">{svc.name}</span>
                <span className="shrink-0 text-sm font-semibold text-yellow-700">{fmtPrice(svc.priceVnd)}</span>
              </div>
            );
          })}
        </div>
        <div className="mt-5 text-center">
          <button onClick={() => booking.openBooking()}
            className="rounded-full bg-yellow-700 px-6 py-2 text-sm font-medium text-white hover:bg-yellow-800">
            Đặt lịch ngay
          </button>
        </div>
      </div>
    </section>
  );
}

function PremiumMedicalWhyUs({ booking }: { booking: BookingProvider }) {
  const cards = [
    { icon: ShieldCheck, title: "Bác sĩ được xác minh", desc: "100% bác sĩ có chứng chỉ hành nghề hợp lệ và hồ sơ năng lực minh bạch." },
    { icon: Star,        title: "Trải nghiệm cao cấp",  desc: "Không gian riêng tư, đặt lịch theo giờ cố định — không phải chờ đợi." },
    { icon: Clock,       title: "Kết quả đúng hẹn",     desc: "Cam kết thời gian trả kết quả — lịch sử khám và toa thuốc lưu đầy đủ." },
  ];
  return (
    <section className="relative overflow-hidden px-4 py-8 sm:px-6">
      <img src={PREMIUM_HERO_PHOTO} alt="" aria-hidden={true} className="absolute inset-0 size-full object-cover" style={{ filter: "blur(12px)", opacity: 0.06, transform: "scale(1.15)" }} />
      <div className="absolute inset-0 bg-yellow-50/90" />
      <div className="relative z-10 mx-auto max-w-5xl">
        <h2 className="mb-5 text-center text-lg font-bold text-slate-900">Vì sao chọn chúng tôi</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {cards.map((c) => (
            <div key={c.title} className="rounded-2xl border border-yellow-200 bg-amber-50 p-4 shadow-sm">
              <span className="flex size-11 items-center justify-center rounded-xl bg-yellow-100">
                <c.icon className="size-5 text-yellow-700" />
              </span>
              <h3 className="mt-4 font-bold text-slate-900">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Button onClick={() => booking.openBooking()} className="rounded-full bg-yellow-700 text-white hover:bg-yellow-800">
            <Calendar className="mr-2 size-4" />Đặt lịch tư vấn ngay
          </Button>
        </div>
      </div>
    </section>
  );
}

function PremiumMedicalStats({ data }: { data: SitePayload["data"] }) {
  const items = [
    { count: "10,000+",  label: "Bệnh nhân tin tưởng" },
    { count: `${data.doctors?.length ?? 0}+`,   label: "Bác sĩ chuyên khoa" },
    { count: "5+",       label: "Năm kinh nghiệm" },
    { count: `${data.specialties?.length ?? 0}+`, label: "Chuyên khoa" },
  ];
  return (
    <section className="bg-slate-900 px-4 py-8 sm:px-6">
      <div className="mx-auto grid max-w-5xl grid-cols-2 gap-6 sm:grid-cols-4">
        {items.map((s) => (
          <div key={s.label} className="text-center">
            <div className="text-3xl font-bold text-yellow-500">{s.count}</div>
            <div className="mt-1 text-xs text-slate-400">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PremiumMedicalAbout({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const points = [
    "Bác sĩ chuyên khoa sâu với chứng chỉ hành nghề đầy đủ",
    "Trang thiết bị hiện đại, quy trình khám chuẩn quốc tế",
    "Không gian riêng tư, bảo mật thông tin y tế tuyệt đối",
  ];
  return (
    <section className="bg-amber-50 px-4 py-8 sm:px-6">
      <div className="mx-auto grid max-w-5xl items-center gap-8 lg:grid-cols-2">
        <img src={FACILITY_PHOTOS[0].url} alt="" className="aspect-square w-full rounded-2xl object-cover" loading="lazy" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-yellow-700">VỀ CHÚNG TÔI</p>
          <h2 className="mt-2 text-2xl font-bold text-slate-900">{payload.clinicName}</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Phòng khám đa khoa cao cấp, quy tụ đội ngũ bác sĩ giàu kinh nghiệm và trang thiết bị tiên tiến. Chúng tôi cam kết mang đến trải nghiệm y tế chuyên nghiệp, riêng tư và tận tâm.
          </p>
          <ul className="mt-4 space-y-2">
            {points.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-yellow-600" />{p}
              </li>
            ))}
          </ul>
          <Button className="mt-5 rounded-full bg-yellow-700 hover:bg-yellow-800" onClick={() => booking.openBooking()}>
            <Calendar className="mr-2 size-4" />Đặt lịch tư vấn
          </Button>
        </div>
      </div>
    </section>
  );
}

function PremiumMedicalBranches({ branches }: { branches: NonNullable<SitePayload["data"]["branches"]> }) {
  if (!branches.length) return null;
  return (
    <section id="sec-contact" className="bg-slate-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <h2 className="mb-4 text-base font-bold text-slate-900">Địa chỉ phòng khám</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {branches.map((b) => (
            <div key={b.id} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <Building2 className="mt-0.5 size-5 shrink-0 text-yellow-700" />
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-slate-900 truncate">{b.name}</h3>
                {b.address && <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">{b.address}</p>}
                {b.phone && (
                  <a href={`tel:${b.phone}`} className="mt-1 flex items-center gap-1 text-xs font-medium text-yellow-700 hover:underline">
                    <Phone className="size-3" />{b.phone}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PremiumMedicalFooter({ payload }: { payload: SitePayload }) {
  const b = payload.data.branches?.[0];
  const specialties = payload.data.specialties ?? [];
  return (
    <footer className="bg-slate-900 px-4 py-7 sm:px-6">
      <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2">
            <span className="flex size-8 items-center justify-center rounded-lg bg-yellow-600 text-sm font-bold text-white">
              {payload.clinicName.charAt(0).toUpperCase()}
            </span>
            <strong className="text-white">{payload.clinicName}</strong>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-slate-400">
            Phòng khám đa khoa cao cấp — cam kết chất lượng và sự riêng tư cho từng bệnh nhân.
          </p>
          {b?.address && (
            <p className="mt-3 flex items-start gap-2 text-xs text-slate-400">
              <MapPin className="mt-0.5 size-3.5 shrink-0 text-yellow-500" />{b.address}
            </p>
          )}
          {b?.phone && (
            <p className="mt-1 flex items-center gap-2 text-xs text-slate-400">
              <Phone className="size-3.5 shrink-0 text-yellow-500" />
              <a href={`tel:${b.phone}`} className="hover:text-white">{b.phone}</a>
            </p>
          )}
        </div>
        {specialties.length > 0 && (
          <div>
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Chuyên khoa</h4>
            <ul className="space-y-1.5">
              {specialties.slice(0, 5).map((s) => (
                <li key={s.id} className="text-xs text-slate-400">{s.name}</li>
              ))}
            </ul>
          </div>
        )}
        <div>
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Liên kết</h4>
          <ul className="space-y-1.5">
            {[
              { label: "Chuyên khoa", id: "sec-specialties" },
              { label: "Bác sĩ", id: "sec-doctors" },
              { label: "Dịch vụ & Giá", id: "sec-services" },
              { label: "Địa chỉ", id: "sec-contact" },
            ].map((lk) => (
              <li key={lk.label}>
                <button onClick={() => jump(lk.id)} className="text-xs text-slate-400 hover:text-white">{lk.label}</button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mx-auto mt-8 max-w-6xl border-t border-slate-800 pt-5 text-center text-xs text-slate-600">
        © {new Date().getFullYear()} {payload.clinicName} · Website tạo bởi Addy HIS
      </div>
    </footer>
  );
}

// Per-theme accent colours (CSS custom-property overrides)
const PREMIUM_MEDICAL_ACCENT: CSSProperties = {
  "--primary": "38 75% 45%",       // antique gold
  "--primary-foreground": "222 47% 11%",
} as CSSProperties;

const SPECIALIST_ACCENT: CSSProperties = {
  "--primary": "213 85% 56%",      // medical blue
  "--primary-foreground": "0 0% 100%",
} as CSSProperties;

const DIAGNOSTIC_ACCENT: CSSProperties = {
  "--primary": "192 78% 38%",      // clinical teal
  "--primary-foreground": "0 0% 100%",
} as CSSProperties;

const DENTAL_BEAUTY_ACCENT: CSSProperties = {
  "--primary": "330 42% 48%",      // dusty rose
  "--primary-foreground": "0 0% 100%",
} as CSSProperties;

const FAMILY_CLINIC_ACCENT: CSSProperties = {
  "--primary": "356 72% 54%",      // warm coral-red
  "--primary-foreground": "0 0% 100%",
} as CSSProperties;

function PremiumMedicalSite({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const raw = payload.data;
  const isDemo = payload.slug?.toUpperCase() === "DEMO";
  const d = {
    ...raw,
    specialties: isDemo ? PREMIUM_DEMO_SPECIALTIES : (raw.specialties?.length ? raw.specialties : PREMIUM_DEMO_SPECIALTIES),
    services:    isDemo ? PREMIUM_DEMO_SERVICES    : (raw.services?.length    ? raw.services    : PREMIUM_DEMO_SERVICES),
  };
  return (
    <div style={PREMIUM_MEDICAL_ACCENT}>
      <PremiumMedicalTopBar payload={payload} />
      <SiteNav payload={payload} booking={booking} />
      <main>
        <PremiumMedicalHero payload={payload} booking={booking} />
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <hr className="border-yellow-200" />
        </div>
        <PremiumMedicalDoctors items={d.doctors ?? []} booking={booking} />
        <PremiumMedicalServices items={d.services ?? []} booking={booking} />
        <PremiumMedicalWhyUs booking={booking} />
        <PremiumMedicalStats data={d} />
        <PremiumMedicalAbout payload={payload} booking={booking} />
        <PremiumMedicalBranches branches={d.branches ?? []} />
      </main>
      <PremiumMedicalFooter payload={payload} />
    </div>
  );
}

// ─── SpecialistSite sub-components ──────────────────────────────────────────

function SpecialistTopBar({ payload }: { payload: SitePayload }) {
  const phone = payload.data.branches?.[0]?.phone;
  return (
    <div className="bg-blue-900 px-4 py-2 text-center text-xs text-blue-100">
      <span className="inline-flex items-center gap-1.5"><BadgeCheck className="size-3.5" />Tất cả bác sĩ được xác minh chứng chỉ hành nghề — hồ sơ minh bạch</span>
      {phone && (
        <span className="ml-2 hidden sm:inline">
          · Hotline:{" "}
          <a href={`tel:${phone}`} className="font-bold text-white underline">
            {phone}
          </a>
        </span>
      )}
    </div>
  );
}

function SpecialistNav({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const links = [
    { label: "Bác sĩ",       target: "sec-doctors"     },
    { label: "Chuyên khoa",  target: "sec-specialties"  },
    { label: "Dịch vụ",      target: "sec-services"    },
    { label: "Về chúng tôi", target: "sec-about"       },
    { label: "Liên hệ",      target: "sec-contact"     },
  ];
  return (
    <header className="sticky top-0 z-50 border-b border-slate-700/50 bg-slate-900/95 shadow-lg backdrop-blur-sm">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
        <a href={`/site/${payload.slug}`} className="flex items-center gap-2.5">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-sm font-bold text-white">
            {payload.clinicName.charAt(0).toUpperCase()}
          </span>
          <span className="hidden font-bold text-white sm:block">{payload.clinicName}</span>
        </a>
        <nav className="hidden items-center gap-6 lg:flex">
          {links.map((l) => (
            <button key={l.label} onClick={() => jump(l.target)}
              className="text-sm font-medium text-slate-300 transition-colors hover:text-blue-400">
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={() => booking.openBooking()}
            className="hidden items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 sm:flex">
            <Calendar className="size-4" />Đặt lịch khám
          </button>
          <button onClick={() => setMenuOpen(!menuOpen)}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 lg:hidden" aria-label="Menu">
            {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="border-t border-slate-700/50 bg-slate-900 px-4 pb-4 pt-3 lg:hidden">
          <div className="space-y-1">
            {links.map((l) => (
              <button key={l.label} onClick={() => { jump(l.target); setMenuOpen(false); }}
                className="block w-full rounded-lg px-3 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-800 hover:text-blue-400">
                {l.label}
              </button>
            ))}
          </div>
          <button onClick={() => { booking.openBooking(); setMenuOpen(false); }}
            className="mt-3 w-full rounded-full bg-blue-600 py-3 text-sm font-semibold text-white">
            Đặt lịch khám
          </button>
        </div>
      )}
    </header>
  );
}

function SpecialistTrustStrip() {
  const items = [
    { icon: BadgeCheck,  text: "100% bác sĩ được xác minh" },
    { icon: Calendar,    text: "Đặt lịch theo giờ cố định"  },
    { icon: ShieldCheck, text: "Giá niêm yết minh bạch"     },
    { icon: Clock,       text: "Không phải chờ đợi"          },
  ];
  return (
    <section className="border-b border-blue-100 bg-blue-50 px-4 py-5 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 sm:justify-between">
          {items.map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-sm text-slate-600">
              <item.icon className="size-4 shrink-0 text-blue-600" />
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Specialist: Hero — 2-col split, doctor-forward ────────────────────────────
function SpecialistHero({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const d = payload.data;
  const thumbs = (d.doctors ?? []).slice(0, 4);
  const trustPoints = [
    "Bác sĩ chuyên khoa được xác minh 100%",
    "Giá niêm yết rõ ràng, không phát sinh",
    "Đặt lịch theo giờ cố định, không chờ",
  ];
  return (
    <section className="relative overflow-hidden px-4 py-12 sm:px-6 sm:py-16">
      <img src={SPECIALIST_HERO_PHOTO} alt="" className="absolute inset-0 size-full object-cover opacity-30" />
      <div className="absolute inset-0"
        style={{ background: "linear-gradient(to bottom right, rgba(15,23,42,0.92), rgba(23,37,84,0.88), rgba(15,23,42,0.85))" }} />
      <div className="relative z-10 mx-auto max-w-6xl">
        <div className="grid gap-12 lg:grid-cols-[1fr_auto] lg:items-center">
          {/* Left: headline + trust + CTA */}
          <div className="max-w-xl">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-400/40 bg-blue-500/15 px-4 py-1.5 text-sm font-medium text-blue-300 backdrop-blur-sm">
              <BadgeCheck className="size-4" />Phòng khám chuyên khoa
            </div>
            <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl">
              Bác sĩ chuyên khoa —<br />hồ sơ minh bạch
            </h1>
            <p className="mt-4 text-base leading-relaxed text-white/75">
              Kết nối trực tiếp với bác sĩ chuyên khoa phù hợp — đặt lịch theo giờ cố định, giá niêm yết rõ ràng, không phải chờ đợi.
            </p>
            <ul className="mt-5 space-y-2">
              {trustPoints.map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-white/80">
                  <CheckCircle2 className="size-4 shrink-0 text-blue-400" />{t}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => booking.openBooking()}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700">
                <Calendar className="size-4" />Đặt lịch ngay
              </button>
              <button onClick={() => jump("sec-doctors")}
                className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/20">
                Xem bác sĩ <ArrowRight className="size-4" />
              </button>
            </div>
            {thumbs.length > 0 && (
              <div className="mt-6 flex items-center gap-3">
                <div className="flex -space-x-2.5">
                  {thumbs.map((doc, i) => (
                    <img key={doc.id} src={DOCTOR_PHOTOS[i % DOCTOR_PHOTOS.length]} alt=""
                      className="size-9 rounded-full border-2 border-white object-cover object-top shadow-sm" />
                  ))}
                </div>
                <span className="text-sm text-white/70">{d.doctors?.length ?? 0}+ bác sĩ chuyên khoa</span>
              </div>
            )}
          </div>
          {/* Right: clinic credential card — desktop only */}
          <div className="hidden w-64 lg:block">
            <div className="rounded-2xl border border-blue-500/30 bg-blue-950/60 p-6 backdrop-blur-sm">
              <div className="mb-5 flex items-center gap-2 border-b border-blue-700/40 pb-4">
                <BadgeCheck className="size-5 text-blue-400" />
                <p className="text-xs font-semibold uppercase tracking-widest text-blue-300">Thống kê phòng khám</p>
              </div>
              <div className="space-y-4">
                {[
                  { icon: BadgeCheck,   count: `${d.doctors?.length ?? 0}+`,    label: "Bác sĩ được xác minh"  },
                  { icon: ClipboardList, count: `${d.specialties?.length ?? 0}+`, label: "Chuyên khoa"          },
                  { icon: Calendar,    count: "100%",                            label: "Đúng giờ hẹn khám"     },
                  { icon: ShieldCheck, count: "Bảo mật",                         label: "Hồ sơ y tế số hoá"     },
                ].map((s) => (
                  <div key={s.label} className="flex items-center gap-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-blue-600/30">
                      <s.icon className="size-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">{s.count}</p>
                      <p className="text-xs text-blue-300">{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => booking.openBooking()}
                className="mt-6 w-full rounded-full bg-blue-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500">
                Đặt lịch ngay
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Specialist: Doctor grid — photo cards, grouped by specialty ──────────────
const SPECIALIST_DOCTORS_PAGE_SIZE = 8;
const SPECIALIST_DOCTORS_UNCATEGORIZED = "Khác";

function SpecialistDoctorGrid({ items, booking }: {
  items: NonNullable<SitePayload["data"]["doctors"]>;
  booking: BookingProvider;
}) {
  const categories = useMemo(() => {
    const names: string[] = [];
    for (const doc of items) {
      const c = doc.specialty?.trim() || SPECIALIST_DOCTORS_UNCATEGORIZED;
      if (!names.includes(c)) names.push(c);
    }
    return names;
  }, [items]);

  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  if (!items.length) return null;

  const filtered = activeCategory
    ? items.filter((d) => (d.specialty?.trim() || SPECIALIST_DOCTORS_UNCATEGORIZED) === activeCategory)
    : items;
  const visible = expanded ? filtered : filtered.slice(0, SPECIALIST_DOCTORS_PAGE_SIZE);
  const remaining = filtered.length - visible.length;

  function selectCategory(c: string | null) {
    setActiveCategory(c);
    setExpanded(false);
  }

  return (
    <section id="sec-doctors" className="bg-slate-800 px-4 py-10 sm:px-6 sm:py-14">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-400">Đội ngũ chuyên gia</p>
          <h2 className="mt-2 text-2xl font-bold text-white sm:text-3xl">Bác sĩ chuyên khoa</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-blue-200">
            Hồ sơ năng lực minh bạch — đặt lịch trực tiếp với bác sĩ bạn chọn.
          </p>
        </div>

        {categories.length > 1 && (
          <div className="mb-8 flex flex-wrap justify-center gap-2">
            <button onClick={() => selectCategory(null)}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                activeCategory === null
                  ? "bg-blue-600 text-white"
                  : "bg-slate-900/70 text-blue-200 hover:bg-slate-900",
              )}>
              Tất cả ({items.length})
            </button>
            {categories.map((c) => {
              const count = items.filter((d) => (d.specialty?.trim() || SPECIALIST_DOCTORS_UNCATEGORIZED) === c).length;
              return (
                <button key={c} onClick={() => selectCategory(c)}
                  className={cn(
                    "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                    activeCategory === c
                      ? "bg-blue-600 text-white"
                      : "bg-slate-900/70 text-blue-200 hover:bg-slate-900",
                  )}>
                  {c} ({count})
                </button>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {visible.map((doc, i) => (
            <div key={doc.id}
              className="group flex flex-col overflow-hidden rounded-2xl border border-blue-800/60 bg-slate-900/70 shadow-sm transition-all hover:-translate-y-1 hover:border-blue-600/60">
              <div className="relative overflow-hidden">
                <img src={DOCTOR_PHOTOS[i % DOCTOR_PHOTOS.length]} alt=""
                  className="h-44 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h3 className="font-bold leading-snug text-white">{doc.name}</h3>
                <span className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-green-400">
                  <BadgeCheck className="size-3.5" />Xác minh
                </span>
                <p className="mt-1 text-xs text-blue-300">{doc.title ?? "Bác sĩ chuyên khoa"}</p>
                {doc.specialty && (
                  <span className="mt-1.5 inline-block w-fit rounded-full bg-blue-900/70 px-2.5 py-0.5 text-[11px] font-medium text-blue-300">
                    {doc.specialty}
                  </span>
                )}
                <div className="flex-1" />
                <button onClick={() => booking.bookDoctor(doc.id)}
                  className="mt-3 w-full rounded-full bg-blue-600 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-500">
                  Đặt lịch
                </button>
              </div>
            </div>
          ))}
        </div>

        {(remaining > 0 || expanded) && (
          <div className="mt-8 text-center">
            {remaining > 0 ? (
              <button onClick={() => setExpanded(true)}
                className="inline-flex items-center gap-2 rounded-full border border-blue-800/60 px-6 py-2.5 text-sm font-medium text-blue-300 transition-colors hover:bg-blue-600 hover:text-white">
                Xem thêm {remaining} bác sĩ <ChevronDown className="size-4" />
              </button>
            ) : (
              <button onClick={() => setExpanded(false)}
                className="inline-flex items-center gap-2 rounded-full border border-blue-800/60 px-6 py-2.5 text-sm font-medium text-blue-300 transition-colors hover:bg-blue-600 hover:text-white">
                Thu gọn <ChevronDown className="size-4 rotate-180" />
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ── Specialist: Trust section — dark bg, points + stats ───────────────────────
function SpecialistTrustSection({ booking }: { booking: BookingProvider }) {
  const points = [
    { icon: BadgeCheck, title: "Bác sĩ 100% được xác minh", desc: "Chứng chỉ hành nghề, kinh nghiệm và năng lực được kiểm tra kỹ lưỡng." },
    { icon: Clock,      title: "Đặt lịch theo giờ cố định", desc: "Chọn bác sĩ, chuyên khoa và khung giờ phù hợp — không phải chờ đợi." },
    { icon: ShieldCheck, title: "Giá niêm yết minh bạch",   desc: "Giá công khai trước khi đặt lịch — không phát sinh chi phí bất ngờ." },
  ];
  const stats = [
    { count: "100%", label: "Bác sĩ xác minh",     sub: "chứng chỉ hành nghề" },
    { count: "5.0★", label: "Đánh giá TB",          sub: "từ bệnh nhân" },
    { count: "Miễn phí", label: "Đặt lịch",         sub: "không mất phí" },
    { count: "Ngay",  label: "Xác nhận tức thì",    sub: "sau khi đặt" },
  ];
  return (
    <section className="relative overflow-hidden px-4 py-10 sm:px-6 sm:py-14">
      <img src={SPECIALIST_HERO_PHOTO} alt="" className="absolute inset-0 size-full object-cover" style={{ filter: "blur(6px)", opacity: 0.45, transform: "scale(1.08)" }} />
      <div className="absolute inset-0"
        style={{ background: "linear-gradient(to bottom right, rgba(15,23,42,0.96), rgba(23,37,84,0.93), rgba(15,23,42,0.96))" }} />
      <div className="relative z-10 mx-auto max-w-6xl">
        <div className="mb-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-400">Cam kết của chúng tôi</p>
          <h2 className="mt-2 text-2xl font-bold text-white sm:text-3xl">Vì sao chọn phòng khám chúng tôi</h2>
        </div>
        <div className="grid gap-8 lg:grid-cols-2">
          {/* Left: trust points */}
          <div className="space-y-6">
            {points.map((p) => (
              <div key={p.title} className="flex gap-4">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-blue-500/25">
                  <p.icon className="size-5 text-blue-300" />
                </div>
                <div>
                  <p className="font-semibold text-white">{p.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/65">{p.desc}</p>
                </div>
              </div>
            ))}
            <button onClick={() => booking.openBooking()}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700">
              <Calendar className="size-4" />Đặt lịch ngay
            </button>
          </div>
          {/* Right: stats 2×2 */}
          <div className="grid grid-cols-2 gap-4">
            {stats.map((s) => (
              <div key={s.label}
                className="rounded-2xl border border-white/15 bg-white/10 p-5 text-center backdrop-blur-sm">
                <div className="text-2xl font-bold text-blue-200">{s.count}</div>
                <div className="mt-1 text-sm font-semibold text-white">{s.label}</div>
                <div className="text-xs text-white/60">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Specialist: Specialty grid — dark clinical directory ─────────────────────
function SpecialistSpecialtyGrid({ items, booking }: {
  items: NonNullable<SitePayload["data"]["specialties"]>;
  booking: BookingProvider;
}) {
  if (!items.length) return null;
  return (
    <section id="sec-specialties" className="bg-slate-900 px-4 py-10 sm:px-6 sm:py-14">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-400">Chuyên khoa</p>
            <h2 className="mt-1.5 text-2xl font-bold text-white">Lĩnh vực chuyên môn</h2>
          </div>
          <button onClick={() => booking.openBooking()}
            className="hidden items-center gap-1.5 text-sm font-medium text-blue-400 hover:text-blue-300 sm:flex">
            Đặt lịch <ArrowRight className="size-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((s, i) => {
            const Icon = SP_COLORS[i % SP_COLORS.length].icon;
            return (
              <button key={s.id} onClick={() => booking.openBooking()}
                className="group flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left transition-colors hover:border-blue-500/50 hover:bg-blue-600/15">
                <Icon className="size-4 shrink-0 text-blue-400 transition-colors group-hover:text-blue-300" />
                <span className="text-sm font-medium text-slate-300 transition-colors group-hover:text-white">{s.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Specialist: Service grid — 1 lưới liền mạch, chip chỉ để lọc (không chia nhóm
// theo hàng — chia nhóm khiến mỗi hàng chỉ 1-2 thẻ để lại khoảng trống lớn) ──
const SPECIALIST_SERVICES_UNCATEGORIZED = "Khác";

function SpecialistServiceCard({ svc, photoIdx, booking }: {
  svc: NonNullable<SitePayload["data"]["services"]>[number];
  photoIdx: number;
  booking: BookingProvider;
}) {
  const theme = SVC_THEMES[photoIdx % SVC_THEMES.length];
  const Icon = theme.icon;
  return (
    <div className="group flex w-56 shrink-0 snap-start flex-col overflow-hidden rounded-xl border border-blue-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-blue-100">
        <img src={SVC_PHOTOS[photoIdx % SVC_PHOTOS.length]} alt="" loading="lazy"
          className="size-full object-cover transition-transform duration-300 group-hover:scale-105" />
        <div className={cn("absolute bottom-2 left-2 flex size-7 items-center justify-center rounded-md text-white shadow-sm", theme.badge)}>
          <Icon className="size-3.5" />
        </div>
      </div>
      <div className="flex flex-1 flex-col p-3">
        <h3 className="text-sm font-semibold leading-snug text-slate-900">{svc.name}</h3>
        {svc.description && (
          <p className="mt-0.5 line-clamp-1 flex-1 text-xs leading-relaxed text-slate-500">{svc.description}</p>
        )}
        <div className="mt-2 flex items-center justify-between border-t border-blue-100 pt-2">
          <span className="whitespace-nowrap text-xs tabular-nums text-blue-600">
            {svc.priceVnd != null ? `Từ ${fmtPrice(svc.priceVnd)}` : ""}
          </span>
          <button
            onClick={() => booking.bookService(svc.id)}
            className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-slate-400 transition-colors hover:text-blue-600"
          >
            Xem chi tiết <ArrowRight className="size-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function SpecialistServiceCards({ items, booking }: {
  items: NonNullable<SitePayload["data"]["services"]>;
  booking: BookingProvider;
}) {
  const categories = useMemo(() => {
    const names: string[] = [];
    for (const svc of items) {
      const c = svc.category?.trim() || SPECIALIST_SERVICES_UNCATEGORIZED;
      if (!names.includes(c)) names.push(c);
    }
    return names;
  }, [items]);

  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  if (!items.length) return null;

  const filtered = activeCategory
    ? items.filter((s) => (s.category?.trim() || SPECIALIST_SERVICES_UNCATEGORIZED) === activeCategory)
    : items;

  return (
    <section id="sec-services" className="bg-blue-50 px-4 py-10 sm:px-6 sm:py-14">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-600">Dịch vụ</p>
            <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">Dịch vụ khám chữa bệnh</h2>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-400">
            {items.length} dịch vụ
          </span>
        </div>

        {categories.length > 1 && (
          <div className="mb-6 flex flex-wrap gap-2">
            <button onClick={() => setActiveCategory(null)}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                activeCategory === null
                  ? "bg-blue-600 text-white"
                  : "bg-blue-100 text-slate-600 hover:bg-blue-200",
              )}>
              Tất cả ({items.length})
            </button>
            {categories.map((c) => {
              const count = items.filter((s) => (s.category?.trim() || SPECIALIST_SERVICES_UNCATEGORIZED) === c).length;
              return (
                <button key={c} onClick={() => setActiveCategory(c)}
                  className={cn(
                    "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                    activeCategory === c
                      ? "bg-blue-600 text-white"
                      : "bg-blue-100 text-slate-600 hover:bg-blue-200",
                  )}>
                  {c} ({count})
                </button>
              );
            })}
          </div>
        )}

        <CarouselRow>
          {filtered.map((svc, i) => (
            <SpecialistServiceCard key={svc.id} svc={svc} photoIdx={i} booking={booking} />
          ))}
        </CarouselRow>

        <div className="mt-6 text-center">
          <button
            onClick={() => booking.openBooking()}
            className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            <Calendar className="size-4" />Đặt lịch tư vấn ngay
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Specialist: Stats band ────────────────────────────────────────────────────
function SpecialistStatsBand({ data }: { data: SitePayload["data"] }) {
  const items = [
    { count: `${data.doctors?.length ?? 0}+`,     label: "Bác sĩ được xác minh" },
    { count: `${data.services?.length ?? 0}+`,    label: "Dịch vụ khám chữa bệnh" },
    { count: `${data.specialties?.length ?? 0}+`, label: "Chuyên khoa" },
    { count: "100%",                               label: "Giá niêm yết rõ ràng" },
  ];
  return (
    <section className="bg-blue-700 px-4 py-7 sm:px-6">
      <div className="mx-auto grid max-w-5xl grid-cols-2 gap-6 sm:grid-cols-4">
        {items.map((s) => (
          <div key={s.label} className="text-center">
            <div className="text-4xl font-bold tabular-nums text-white">{s.count}</div>
            <div className="mt-1.5 text-xs font-semibold uppercase tracking-wide text-blue-200">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Specialist: FAQ data ──────────────────────────────────────────────────────
const SPECIALIST_FAQ = [
  { q: "Làm thế nào để đặt lịch với bác sĩ chuyên khoa?",    a: "Chọn bác sĩ hoặc chuyên khoa trên website, chọn khung giờ phù hợp — xác nhận lịch hẹn gửi ngay lập tức qua SMS hoặc email." },
  { q: "Có thể chọn bác sĩ cụ thể để khám không?",           a: "Có. Bạn xem hồ sơ năng lực từng bác sĩ và đặt lịch trực tiếp theo khung giờ cố định của bác sĩ đó — không bị phân công ngẫu nhiên." },
  { q: "Giá khám có bao gồm xét nghiệm không?",              a: "Giá khám chuyên khoa được niêm yết chưa bao gồm xét nghiệm hoặc chẩn đoán hình ảnh nếu bác sĩ chỉ định. Chi phí phát sinh thông báo trước khi thực hiện." },
  { q: "Kết quả khám được lưu trữ ở đâu?",                   a: "Toàn bộ kết quả khám, toa thuốc và lịch sử tái khám lưu trong hệ thống — bạn có thể truy cập bất cứ lúc nào qua tài khoản bệnh nhân." },
  { q: "Có thể đổi hoặc hủy lịch không?",                    a: "Có. Đổi hoặc hủy lịch trước ít nhất 2 giờ qua website hoặc hotline — không mất phí." },
  { q: "Phòng khám có tiếp nhận bảo hiểm y tế không?",       a: "Vui lòng liên hệ hotline hoặc đến trực tiếp cơ sở để được tư vấn về các loại bảo hiểm được chấp nhận." },
  { q: "Thời gian chờ trung bình là bao lâu?",               a: "Với lịch đặt theo giờ cố định, thời gian chờ thường dưới 10 phút. Trường hợp vãng lai phụ thuộc vào chỗ trống trong ngày." },
] as const;

// ── Specialist: Testimonials ──────────────────────────────────────────────────
const SPECIALIST_TESTIMONIALS = [
  { initials: "NV", role: "Bệnh nhân khám nội khoa",  quote: "Bác sĩ giải thích rõ ràng, kết quả khám được lưu đầy đủ, tái khám rất tiện." },
  { initials: "TH", role: "Bệnh nhân khám tim mạch",  quote: "Đặt lịch qua website, đến đúng giờ được khám ngay — không mất thời gian chờ." },
  { initials: "ML", role: "Bệnh nhân khám thần kinh", quote: "Bác sĩ nhiều kinh nghiệm, hồ sơ minh bạch — tôi hoàn toàn tin tưởng." },
] as const;

function SpecialistTestimonials() {
  const [idx, setIdx] = useState(0);
  const total = SPECIALIST_TESTIMONIALS.length;
  const t = SPECIALIST_TESTIMONIALS[idx];
  function go(dir: 1 | -1) { setIdx((i) => (i + dir + total) % total); }
  return (
    <section className="bg-blue-950 px-4 py-10 sm:px-6 sm:py-14">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-400">Đánh giá từ bệnh nhân</p>
          <h2 className="mt-2 text-2xl font-bold text-white sm:text-3xl">Bệnh nhân nói gì về chúng tôi</h2>
        </div>
        <div className="relative rounded-2xl border border-blue-700/40 bg-slate-900/70 p-8 sm:p-10">
          <span className="absolute left-6 top-3 select-none font-serif text-7xl leading-none text-blue-800">"</span>
          <div className="flex justify-center gap-1 mb-5">
            {[1,2,3,4,5].map((n) => <Star key={n} className="size-5 fill-amber-400 text-amber-400" />)}
          </div>
          <p className="relative z-10 text-center text-lg leading-relaxed text-white/90 italic">"{t.quote}"</p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-blue-700 text-sm font-bold text-white">
              {t.initials.charAt(0)}
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-white">{t.initials}.</p>
              <p className="text-xs text-blue-300">{t.role}</p>
            </div>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-center gap-4">
          <button onClick={() => go(-1)}
            className="flex size-9 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10">
            <ChevronLeft className="size-4" />
          </button>
          <div className="flex gap-2">
            {SPECIALIST_TESTIMONIALS.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)}
                className={cn("h-2 rounded-full transition-all", i === idx ? "w-6 bg-blue-400" : "w-2 bg-white/30")} />
            ))}
          </div>
          <button onClick={() => go(1)}
            className="flex size-9 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10">
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Specialist: About — 2-col image + feature list ───────────────────────────
function SpecialistAbout({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const points = [
    { icon: BadgeCheck,  title: "Bác sĩ được xác minh 100%",    desc: "Chứng chỉ hành nghề, kinh nghiệm và năng lực kiểm tra kỹ lưỡng trước khi đăng tải hồ sơ." },
    { icon: ClipboardList, title: "Hồ sơ bệnh án số hoá",       desc: "Kết quả khám, toa thuốc và lịch sử tái khám lưu trữ đầy đủ, truy cập bất cứ lúc nào." },
    { icon: ShieldCheck, title: "Bảo mật thông tin bệnh nhân",   desc: "Dữ liệu y tế được mã hoá và bảo vệ theo tiêu chuẩn bảo mật thông tin quốc gia." },
    { icon: Clock,       title: "Đặt lịch theo giờ cố định",     desc: "Không chờ đợi — đến đúng giờ hẹn, khám ngay với bác sĩ đã chọn." },
  ];
  return (
    <section id="sec-about" className="bg-blue-50 px-4 py-10 sm:px-6 sm:py-14">
      <div className="mx-auto max-w-6xl">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="relative">
            <img src={SPECIALIST_HERO_PHOTO} alt=""
              className="aspect-[4/3] w-full rounded-2xl object-cover shadow-md" loading="lazy" />
            <div className="absolute -bottom-4 -right-4 hidden rounded-2xl bg-blue-600 px-6 py-4 text-center text-white shadow-lg lg:block">
              <p className="text-2xl font-bold">100%</p>
              <p className="text-xs text-blue-200">Bác sĩ xác minh</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-600">Về chúng tôi</p>
            <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">{payload.clinicName}</h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              Phòng khám chuyên khoa với đội ngũ bác sĩ có chuyên môn sâu, hồ sơ năng lực minh bạch và quy trình khám chuẩn mực — mang đến trải nghiệm y tế chuyên nghiệp, đúng giờ và tin cậy.
            </p>
            <ul className="mt-6 space-y-4">
              {points.map((p) => (
                <li key={p.title} className="flex gap-3">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-blue-50">
                    <p.icon className="size-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{p.title}</p>
                    <p className="mt-0.5 text-sm text-slate-500">{p.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
            <button onClick={() => booking.openBooking()}
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700">
              <Calendar className="size-4" />Đặt lịch tư vấn
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Specialist: FAQ — accordion left + CTA card right ────────────────────────
function SpecialistFaq({ booking }: { booking: BookingProvider }) {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return (
    <section className="bg-slate-900 px-4 py-10 sm:px-6 sm:py-14">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-6 lg:grid-cols-[3fr_2fr] lg:items-start">
          <div>
            <div className="mb-6 flex items-center gap-2">
              <HelpCircle className="size-5 text-blue-400" />
              <h2 className="text-xl font-bold text-white">Câu hỏi thường gặp</h2>
            </div>
            <div className="space-y-2">
              {SPECIALIST_FAQ.map((item, i) => {
                const isOpen = openIdx === i;
                return (
                  <div key={item.q} className="overflow-hidden rounded-xl border border-white/10 bg-white/5">
                    <button onClick={() => setOpenIdx(isOpen ? null : i)}
                      className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left">
                      <span className="text-sm font-semibold text-slate-200">{item.q}</span>
                      <ChevronDown className={cn("size-4 shrink-0 text-slate-500 transition-transform", isOpen && "rotate-180")} />
                    </button>
                    {isOpen && <p className="px-5 pb-5 text-sm leading-relaxed text-slate-400">{item.a}</p>}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="rounded-2xl bg-blue-600 p-6 text-white">
            <p className="text-lg font-bold">Còn thắc mắc?</p>
            <p className="mt-2 text-sm leading-relaxed text-blue-100">
              Đội ngũ tư vấn sẵn sàng giúp bạn chọn đúng bác sĩ và chuyên khoa phù hợp nhất.
            </p>
            <button onClick={() => booking.openBooking()}
              className="mt-5 w-full rounded-full bg-white py-3 text-sm font-bold text-blue-700 transition-colors hover:bg-blue-50">
              <span className="flex items-center justify-center gap-2">
                <Calendar className="size-4" />Đặt lịch tư vấn
              </span>
            </button>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-white/15 p-3 text-center">
                <p className="text-base font-bold">T2 – T7</p>
                <p className="text-xs text-blue-200">07:00 – 19:00</p>
              </div>
              <div className="rounded-xl bg-white/15 p-3 text-center">
                <p className="text-base font-bold">Chủ nhật</p>
                <p className="text-xs text-blue-200">07:00 – 12:00</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Specialist: Contact — 2-col branches + booking CTA ───────────────────────
function SpecialistContact({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const branches = payload.data.branches ?? [];
  if (!branches.length) return null;
  return (
    <section id="sec-contact" className="bg-blue-50 px-4 py-10 sm:px-6 sm:py-14">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-6 sm:grid-cols-[3fr_2fr] sm:items-start">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-600">Liên hệ</p>
            <h2 className="mt-2 text-xl font-bold text-slate-900">Tìm chúng tôi</h2>
            <div className="mt-5 space-y-3">
              {branches.map((b) => (
                <div key={b.id} className="rounded-2xl border border-blue-100 p-4">
                  <h3 className="font-semibold text-slate-900">{b.name}</h3>
                  {b.address && (
                    <p className="mt-1.5 flex items-start gap-2 text-sm text-slate-600">
                      <MapPin className="mt-0.5 size-4 shrink-0 text-blue-600" />{b.address}
                    </p>
                  )}
                  {b.phone && (
                    <a href={`tel:${b.phone}`} className="mt-1 flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline">
                      <Phone className="size-4 shrink-0" />{b.phone}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
            <p className="font-bold text-slate-900">Đặt lịch ngay</p>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
              Chọn bác sĩ, chuyên khoa và khung giờ phù hợp — xác nhận gửi ngay lập tức.
            </p>
            <button onClick={() => booking.openBooking()}
              className="mt-4 w-full rounded-full bg-blue-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700">
              <span className="flex items-center justify-center gap-2">
                <Calendar className="size-4" />Đặt lịch khám
              </span>
            </button>
            <div className="mt-4 flex items-start gap-2 text-xs text-slate-500">
              <Clock className="mt-0.5 size-3.5 shrink-0 text-blue-600" />
              <span>Thứ 2 – Thứ 7: 07:00–19:00 · Chủ nhật: 07:00–12:00</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Specialist: Final CTA — navy gradient ─────────────────────────────────────
function SpecialistFinalCta({ booking }: { booking: BookingProvider }) {
  return (
    <section className="relative overflow-hidden px-4 py-12 sm:px-6 sm:py-16">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-blue-900" />
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -right-20 -top-20 size-72 rounded-full bg-white/5" />
        <div className="absolute -bottom-16 left-1/4 size-56 rounded-full bg-blue-500/10" />
      </div>
      <div className="relative z-10 mx-auto max-w-2xl text-center">
        <BadgeCheck className="mx-auto mb-4 size-10 text-blue-400" />
        <h2 className="text-2xl font-bold text-white sm:text-3xl">
          Kết nối với bác sĩ chuyên khoa ngay hôm nay
        </h2>
        <p className="mx-auto mt-4 max-w-md text-white/70">
          Đặt lịch theo giờ cố định — đến đúng giờ, khám đúng bác sĩ, giá niêm yết rõ ràng.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button onClick={() => booking.openBooking()}
            className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3 text-sm font-bold text-white shadow-lg transition-colors hover:bg-blue-700">
            <Calendar className="size-4" />Đặt lịch khám ngay
          </button>
          <button onClick={() => jump("sec-doctors")}
            className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/20">
            Xem đội ngũ bác sĩ <ArrowRight className="size-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Specialist: Footer — 4-col ────────────────────────────────────────────────
function SpecialistFooter({ payload }: { payload: SitePayload }) {
  const b = payload.data.branches?.[0];
  const specialties = payload.data.specialties ?? [];
  const services = payload.data.services ?? [];
  return (
    <footer className="bg-slate-900 px-4 py-8 sm:px-6">
      <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {/* Brand */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2.5">
            <span className="flex size-9 items-center justify-center rounded-lg bg-blue-600 text-sm font-bold text-white">
              {payload.clinicName.charAt(0).toUpperCase()}
            </span>
            <strong className="text-lg text-white">{payload.clinicName}</strong>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-slate-400">
            Kết nối bạn với các bác sĩ chuyên khoa — hồ sơ minh bạch, đặt lịch nhanh, giá niêm yết rõ ràng.
          </p>
          {b?.address && (
            <p className="mt-3 flex items-start gap-2 text-xs text-slate-400">
              <MapPin className="mt-0.5 size-3.5 shrink-0 text-blue-400" />{b.address}
            </p>
          )}
          {b?.phone && (
            <p className="mt-1 flex items-center gap-2 text-xs text-slate-400">
              <Phone className="size-3.5 shrink-0 text-blue-400" />
              <a href={`tel:${b.phone}`} className="hover:text-white">{b.phone}</a>
            </p>
          )}
        </div>
        {/* Specialties */}
        {specialties.length > 0 && (
          <div>
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Chuyên khoa</h4>
            <ul className="space-y-2">
              {specialties.slice(0, 6).map((s) => (
                <li key={s.id} className="text-xs text-slate-400 transition-colors hover:text-slate-200 cursor-default">{s.name}</li>
              ))}
            </ul>
          </div>
        )}
        {/* Services */}
        {services.length > 0 && (
          <div>
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Dịch vụ</h4>
            <ul className="space-y-2">
              {services.slice(0, 6).map((s) => (
                <li key={s.id} className="text-xs text-slate-400 line-clamp-1 transition-colors hover:text-slate-200 cursor-default">{s.name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="mx-auto mt-8 max-w-5xl border-t border-slate-800 pt-6 text-center text-xs text-slate-600">
        © {new Date().getFullYear()} {payload.clinicName} · Website tạo bởi Addy HIS
      </div>
    </footer>
  );
}

// ─── Family Clinic theme ────────────────────────────────────────────────────
// Warm family clinic — trustworthy, human, booking-forward. Production redesign.

const FAMILY_TESTIMONIALS = [
  { initials: "N.T.", role: "Phụ huynh bệnh nhi",            quote: "Đặt lịch cho con chỉ mất một phút, bác sĩ khám kỹ và giải thích rất dễ hiểu." },
  { initials: "K.H.", role: "Khách hàng khám định kỳ",       quote: "Không gian thân thiện, không phải chờ lâu — đúng nhu cầu của một gia đình bận rộn." },
  { initials: "M.L.", role: "Người nhà bệnh nhân cao tuổi",  quote: "Đội ngũ điều dưỡng tận tâm, theo dõi sức khoẻ ông bà mình rất chu đáo." },
] as const;

const FAMILY_FAQ = [
  { q: "Có cần đặt lịch trước không?",                         a: "Nên đặt lịch trước qua website hoặc hotline để được ưu tiên khung giờ. Phòng khám vẫn tiếp nhận bệnh nhân vãng lai tuỳ tình trạng chỗ trống." },
  { q: "Phòng khám có khám cho trẻ em không?",                 a: "Có. Đội ngũ bác sĩ nhi khoa khám và tư vấn cho trẻ từ sơ sinh đến 15 tuổi." },
  { q: "Có thể đặt lịch cho nhiều thành viên trong gia đình không?", a: "Có. Bạn có thể đặt nhiều lịch riêng biệt cho từng thành viên trong cùng một lần truy cập website." },
  { q: "Phòng khám có tiêm chủng không?",                      a: "Có. Chúng tôi thực hiện tiêm chủng theo lịch tiêm chuẩn của Bộ Y tế cho trẻ em và người lớn." },
  { q: "Có khám ngoài giờ hành chính không?",                  a: "Phòng khám mở cửa Thứ 2–Thứ 7: 07:00–19:00 và Chủ nhật: 07:00–12:00. Liên hệ hotline để được hỗ trợ thêm." },
  { q: "Có hỗ trợ bảo hiểm y tế không?",                      a: "Vui lòng liên hệ trực tiếp hotline hoặc đến cơ sở để được tư vấn về các loại bảo hiểm y tế được chấp nhận." },
  { q: "Có thể đổi hoặc hủy lịch không?",                     a: "Bạn có thể đổi hoặc hủy lịch trước ít nhất 2 giờ qua website hoặc hotline mà không mất phí." },
] as const;

const FAMILY_CARE_GROUPS = [
  { icon: Baby,        title: "Trẻ em",          color: "bg-sky-50  border-sky-100",   iconBg: "bg-sky-100",   iconColor: "text-sky-600",   items: ["Khám nhi tổng quát", "Tiêm chủng theo lịch", "Theo dõi phát triển"]     },
  { icon: Heart,       title: "Phụ nữ",           color: "bg-rose-50 border-rose-100",  iconBg: "bg-rose-100",  iconColor: "text-rose-600",  items: ["Khám thai định kỳ", "Sức khỏe phụ khoa", "Siêu âm thai nhi"]         },
  { icon: Stethoscope, title: "Người lớn",         color: "bg-amber-50 border-amber-100", iconBg: "bg-amber-100", iconColor: "text-amber-600", items: ["Khám tổng quát", "Tầm soát bệnh lý", "Tư vấn sức khỏe"]           },
  { icon: Activity,    title: "Người cao tuổi",    color: "bg-green-50 border-green-100", iconBg: "bg-green-100", iconColor: "text-green-600", items: ["Bệnh mạn tính", "Kiểm tra định kỳ", "Theo dõi lâu dài"]            },
] as const;

function FamilyTopBar({ payload }: { payload: SitePayload }) {
  const phone = payload.data.branches?.[0]?.phone;
  return (
    <div className="bg-primary px-4 py-2 text-center text-xs text-primary-foreground">
      Chăm sóc sức khỏe cho cả gia đình — đặt lịch dễ dàng, bác sĩ tận tâm
      {phone && (
        <span className="ml-2">
          · Hotline: <a href={`tel:${phone}`} className="font-bold underline">{phone}</a>
        </span>
      )}
    </div>
  );
}

function FamilyNav({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const links = [
    { label: "Chuyên khoa",    target: "sec-specialties" },
    { label: "Bác sĩ",         target: "sec-doctors"     },
    { label: "Dịch vụ",        target: "sec-services"    },
    { label: "Về chúng tôi",   target: "sec-about"       },
    { label: "Liên hệ",        target: "sec-contact"     },
  ];
  return (
    <header className="sticky top-0 z-50 border-b border-amber-200 bg-amber-50/97 shadow-sm backdrop-blur-sm">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
        <a href={`/site/${payload.slug}`} className="flex items-center gap-2.5">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary text-sm font-bold text-white">
            {payload.clinicName.charAt(0).toUpperCase()}
          </span>
          <span className="hidden font-bold text-orange-900 sm:block">{payload.clinicName}</span>
        </a>
        <nav className="hidden items-center gap-7 lg:flex">
          {links.map((l) => (
            <button key={l.label} onClick={() => jump(l.target)}
              className="text-sm text-stone-700 transition-colors hover:text-orange-600">
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={() => booking.openBooking()}
            className="hidden items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 sm:flex">
            <Calendar className="size-4" />Đặt lịch khám
          </button>
          <button onClick={() => setMenuOpen(!menuOpen)}
            className="rounded-lg p-2 text-stone-600 hover:bg-orange-100 lg:hidden"
            aria-label="Menu">
            {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="border-t border-amber-100 bg-amber-50 px-4 pb-4 pt-3 lg:hidden">
          <div className="space-y-1">
            {links.map((l) => (
              <button key={l.label} onClick={() => { jump(l.target); setMenuOpen(false); }}
                className="block w-full rounded-lg px-3 py-2.5 text-left text-sm text-stone-700 hover:bg-orange-50 hover:text-orange-600">
                {l.label}
              </button>
            ))}
          </div>
          <button onClick={() => { booking.openBooking(); setMenuOpen(false); }}
            className="mt-3 w-full rounded-full bg-primary py-3 text-sm font-semibold text-white">
            Đặt lịch khám
          </button>
        </div>
      )}
    </header>
  );
}

function FamilyHero({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const d = payload.data;
  const thumbs = (d.doctors ?? []).slice(0, 4);
  const trustPoints = ["Khám cho trẻ em", "Khám người lớn", "Chăm sóc định kỳ"];
  return (
    <section className="relative overflow-hidden px-4 py-16 sm:px-6 sm:py-20">
      <img src={FAMILY_HERO_PHOTO} alt="" className="absolute inset-0 size-full object-cover" />
      <div className="absolute inset-0"
        style={{ background: "linear-gradient(to bottom right, rgba(67,20,7,0.90), rgba(124,45,18,0.82), rgba(154,52,18,0.78))" }} />
      <div className="relative z-10 mx-auto max-w-6xl">
        <div className="grid gap-12 lg:grid-cols-[1fr_auto] lg:items-center">
          {/* Left: headline + CTA */}
          <div className="max-w-xl">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-1.5 text-sm font-medium text-white/90 backdrop-blur-sm">
              <Heart className="size-4 text-red-300" /> Phòng khám gia đình
            </div>
            <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl">
              Chăm sóc sức khỏe<br />cho cả gia đình
            </h1>
            <p className="mt-4 text-base leading-relaxed text-white/80">
              Đội ngũ bác sĩ đồng hành cùng bạn từ những lần khám đầu tiên đến chăm sóc sức khỏe lâu dài — thân thiện, tận tâm và minh bạch.
            </p>
            <ul className="mt-5 space-y-2">
              {trustPoints.map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-white/85">
                  <CheckCircle2 className="size-4 shrink-0 text-green-400" />{t}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => booking.openBooking()}
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-orange-800 transition-colors hover:bg-orange-50">
                <Calendar className="size-4" />Đặt lịch khám
              </button>
              {(d.doctors?.length ?? 0) > 0 && (
                <button onClick={() => jump("sec-doctors")}
                  className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/20">
                  Xem đội ngũ bác sĩ
                </button>
              )}
            </div>
            {thumbs.length > 0 && (
              <div className="mt-6 flex items-center gap-3">
                <div className="flex -space-x-2.5">
                  {thumbs.map((doc, i) => (
                    <img key={doc.id} src={DOCTOR_PHOTOS[i % DOCTOR_PHOTOS.length]} alt=""
                      className="size-9 rounded-full border-2 border-white object-cover object-top shadow-sm" />
                  ))}
                </div>
                <span className="text-sm text-white/75">{d.doctors?.length ?? 0}+ bác sĩ đồng hành</span>
              </div>
            )}
          </div>

          {/* Right: 2×2 doctor photo collage — desktop only */}
          {thumbs.length >= 2 && (
            <div className="hidden w-60 lg:grid lg:grid-cols-2 lg:gap-2.5">
              {thumbs.slice(0, 4).map((doc, i) => (
                <div key={doc.id} className="relative overflow-hidden rounded-2xl shadow-md">
                  <img src={DOCTOR_PHOTOS[i % DOCTOR_PHOTOS.length]} alt=""
                    className="h-28 w-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-2">
                    <p className="truncate text-[10px] font-semibold text-white">{doc.name}</p>
                    <p className="text-[9px] text-white/70">{doc.specialty ?? "Bác sĩ"}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function FamilyTrustStrip() {
  const items = [
    { icon: BadgeCheck,  text: "Bác sĩ tận tâm"          },
    { icon: Calendar,    text: "Đặt lịch dễ dàng"         },
    { icon: Heart,       text: "Chăm sóc cả gia đình"     },
    { icon: ShieldCheck, text: "Giá niêm yết minh bạch"   },
  ];
  return (
    <section className="border-b border-orange-100 bg-white px-4 py-5 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 sm:justify-between">
          {items.map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-sm text-slate-600">
              <item.icon className="size-4 shrink-0 text-primary" />
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FamilyFeaturedCare({ booking }: { booking: BookingProvider }) {
  return (
    <section className="bg-white px-4 py-16 sm:px-6 sm:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">Chăm sóc toàn diện</p>
          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">Chăm sóc cho mọi thành viên</h2>
          <p className="mx-auto mt-3 max-w-lg text-slate-500">
            Từ trẻ sơ sinh đến người cao tuổi, chúng tôi đồng hành cùng sức khỏe cả gia đình bạn.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FAMILY_CARE_GROUPS.map((group) => (
            <div key={group.title} className={cn("rounded-2xl border p-6 transition-shadow hover:shadow-sm", group.color)}>
              <div className={cn("mb-4 flex size-11 items-center justify-center rounded-xl", group.iconBg)}>
                <group.icon className={cn("size-6", group.iconColor)} />
              </div>
              <h3 className="text-base font-bold text-slate-900">{group.title}</h3>
              <ul className="mt-3 space-y-2">
                {group.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-green-500" />{item}
                  </li>
                ))}
              </ul>
              <button onClick={() => booking.openBooking()}
                className="mt-5 text-sm font-semibold text-primary hover:underline">
                Đặt lịch →
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FamilyBookingSteps({ booking }: { booking: BookingProvider }) {
  const steps = [
    { num: "01", icon: Search,        title: "Chọn bác sĩ hoặc dịch vụ",  desc: "Tìm theo chuyên khoa hoặc tên bác sĩ phù hợp với nhu cầu của bạn."   },
    { num: "02", icon: Calendar,      title: "Chọn ngày giờ phù hợp",      desc: "Xem lịch trống và chọn khung giờ bạn muốn một cách dễ dàng."         },
    { num: "03", icon: CheckCircle2,  title: "Xác nhận lịch khám",         desc: "Nhận thông báo xác nhận ngay lập tức sau khi hoàn tất đặt lịch."     },
  ];
  return (
    <section className="bg-orange-50 px-4 py-16 sm:px-6 sm:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">Quy trình đặt lịch</p>
          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">Đặt lịch chỉ 3 bước</h2>
        </div>
        <div className="grid gap-8 sm:grid-cols-3">
          {steps.map((s, i) => (
            <div key={s.num} className="flex flex-col items-center text-center">
              {/* Icon box */}
              <div className="relative mb-5 flex size-20 items-center justify-center rounded-2xl bg-primary/10">
                <s.icon className="size-9 text-primary" />
                <span className="absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-white">
                  {s.num}
                </span>
              </div>
              {/* Connector */}
              {i < steps.length - 1 && (
                <ChevronRight className="absolute hidden text-orange-200 sm:block" style={{ right: "-1rem", top: "2.5rem" }} />
              )}
              <h3 className="text-base font-bold text-slate-900">{s.title}</h3>
              <p className="mt-2 max-w-[220px] text-sm leading-relaxed text-slate-500">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <button onClick={() => booking.openBooking()}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary/90">
            <Calendar className="size-4" />Đặt lịch ngay
          </button>
        </div>
      </div>
    </section>
  );
}

const FAMILY_SERVICES_PAGE_SIZE = 6;
const FAMILY_SERVICES_UNCATEGORIZED = "Khác";

function FamilyServices({ items, booking }: {
  items: NonNullable<SitePayload["data"]["services"]>;
  booking: BookingProvider;
}) {
  const categories = useMemo(() => {
    const names: string[] = [];
    for (const svc of items) {
      const c = svc.category?.trim() || FAMILY_SERVICES_UNCATEGORIZED;
      if (!names.includes(c)) names.push(c);
    }
    return names;
  }, [items]);

  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  if (!items.length) return null;

  const filtered = activeCategory
    ? items.filter((s) => (s.category?.trim() || FAMILY_SERVICES_UNCATEGORIZED) === activeCategory)
    : items;
  const visible = expanded ? filtered : filtered.slice(0, FAMILY_SERVICES_PAGE_SIZE);
  const remaining = filtered.length - visible.length;

  function selectCategory(c: string | null) {
    setActiveCategory(c);
    setExpanded(false);
  }

  return (
    <section id="sec-services" className="bg-white px-4 py-16 sm:px-6 sm:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">Dịch vụ</p>
          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">Dịch vụ chăm sóc sức khỏe</h2>
          <p className="mx-auto mt-3 max-w-lg text-slate-500">
            Đa dạng dịch vụ cho cả gia đình — từ khám tổng quát đến theo dõi sức khỏe lâu dài.
          </p>
        </div>

        {categories.length > 1 && (
          <div className="mb-8 flex flex-wrap justify-center gap-2">
            <button onClick={() => selectCategory(null)}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                activeCategory === null
                  ? "bg-primary text-white"
                  : "bg-orange-50 text-slate-600 hover:bg-orange-100",
              )}>
              Tất cả ({items.length})
            </button>
            {categories.map((c) => {
              const count = items.filter((s) => (s.category?.trim() || FAMILY_SERVICES_UNCATEGORIZED) === c).length;
              return (
                <button key={c} onClick={() => selectCategory(c)}
                  className={cn(
                    "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                    activeCategory === c
                      ? "bg-primary text-white"
                      : "bg-orange-50 text-slate-600 hover:bg-orange-100",
                  )}>
                  {c} ({count})
                </button>
              );
            })}
          </div>
        )}

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((svc, i) => {
            const theme = SVC_THEMES[i % SVC_THEMES.length];
            const Icon = theme.icon;
            return (
              <div key={svc.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-sm transition-shadow hover:shadow-md">
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-orange-100">
                  <img src={SVC_PHOTOS[i % SVC_PHOTOS.length]} alt="" loading="lazy"
                    className="size-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className={cn("absolute bottom-3 left-3 flex size-9 items-center justify-center rounded-lg text-white shadow-sm", theme.badge)}>
                    <Icon className="size-4" />
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="text-base font-bold leading-snug text-slate-900">{svc.name}</h3>
                  {svc.description && (
                    <p className="mt-1.5 flex-1 text-sm leading-relaxed text-slate-500">{svc.description}</p>
                  )}
                  <div className="mt-4 flex items-center justify-between border-t border-orange-200 pt-4">
                    <span className="text-sm font-semibold text-primary">{fmtPrice(svc.priceVnd)}</span>
                    <button onClick={() => booking.bookService(svc.id)}
                      className="inline-flex items-center gap-1 text-sm font-medium text-slate-400 transition-colors hover:text-primary">
                      Đặt lịch <ArrowRight className="size-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {(remaining > 0 || expanded) && (
          <div className="mt-8 text-center">
            {remaining > 0 ? (
              <button onClick={() => setExpanded(true)}
                className="inline-flex items-center gap-2 rounded-full border border-orange-200 px-6 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-white">
                Xem thêm {remaining} dịch vụ <ChevronDown className="size-4" />
              </button>
            ) : (
              <button onClick={() => setExpanded(false)}
                className="inline-flex items-center gap-2 rounded-full border border-orange-200 px-6 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-white">
                Thu gọn <ChevronDown className="size-4 rotate-180" />
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function FamilyDoctors({ items, booking }: {
  items: NonNullable<SitePayload["data"]["doctors"]>;
  booking: BookingProvider;
}) {
  if (!items.length) return null;
  // Cap at 4: 2-col mobile → 4-col md+, no orphan at any breakpoint
  const displayed = items.slice(0, 4);
  const extra = items.length - displayed.length;
  return (
    <section id="sec-doctors" className="bg-orange-50 px-4 py-16 sm:px-6 sm:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">Đội ngũ</p>
          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">Bác sĩ của chúng tôi</h2>
          <p className="mx-auto mt-3 max-w-lg text-slate-500">
            Mỗi bác sĩ là người đồng hành đáng tin cậy — tận tâm, am hiểu và luôn lắng nghe.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {displayed.map((doc, i) => (
            <div key={doc.id}
              className="group flex flex-col overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-md">
              <div className="relative overflow-hidden">
                <img src={DOCTOR_PHOTOS[i % DOCTOR_PHOTOS.length]} alt=""
                  className="h-48 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="flex flex-1 flex-col p-4">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{doc.title ?? "Bác sĩ"}</p>
                <h3 className="mt-0.5 font-bold leading-snug text-slate-900">{doc.name}</h3>
                {doc.specialty && (
                  <span className="mt-1.5 inline-block rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary">
                    {doc.specialty}
                  </span>
                )}
                <div className="flex-1" />
              </div>
              <div className="grid grid-cols-2 divide-x divide-orange-100 border-t border-orange-100">
                <button onClick={() => jump("sec-about")}
                  className="py-2.5 text-xs font-medium text-slate-500 transition-colors hover:bg-orange-50 hover:text-primary">
                  Hồ sơ
                </button>
                <button onClick={() => booking.bookDoctor(doc.id)}
                  className="py-2.5 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-white">
                  Đặt lịch
                </button>
              </div>
            </div>
          ))}
        </div>
        {extra > 0 && (
          <div className="mt-8 text-center">
            <button onClick={() => booking.openBooking()}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
              Xem thêm {extra} bác sĩ khác <ArrowRight className="size-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function FamilyAbout({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const points = [
    { icon: BadgeCheck,  title: "Bác sĩ được xác minh",    desc: "100% bác sĩ có chứng chỉ hành nghề và hồ sơ năng lực minh bạch." },
    { icon: Heart,       title: "Chăm sóc cả gia đình",     desc: "Từ trẻ sơ sinh đến người cao tuổi — chúng tôi đồng hành lâu dài." },
    { icon: ShieldCheck, title: "Giá niêm yết rõ ràng",     desc: "Không phát sinh chi phí bất ngờ — minh bạch trước mỗi lần khám." },
    { icon: Clock,       title: "Đặt lịch linh hoạt",       desc: "Đặt lịch 24/7 qua website, lịch sử khám được lưu trữ đầy đủ." },
  ];
  return (
    <section id="sec-about" className="bg-white px-4 py-16 sm:px-6 sm:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          {/* Left: clinic photo */}
          <div className="relative">
            <img src={FAMILY_HERO_PHOTO} alt=""
              className="aspect-[4/3] w-full rounded-2xl object-cover shadow-md" loading="lazy" />
            <div className="absolute -bottom-4 -right-4 hidden rounded-2xl bg-primary px-6 py-4 text-center text-white shadow-lg lg:block">
              <p className="text-2xl font-bold">5+</p>
              <p className="text-xs text-white/80">Năm phục vụ</p>
            </div>
          </div>
          {/* Right: about text + feature list */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">Về chúng tôi</p>
            <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">{payload.clinicName}</h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              Phòng khám gia đình với đội ngũ bác sĩ tận tâm và môi trường thân thiện — nơi mỗi thành viên trong gia đình đều được chăm sóc chu đáo, từ lần khám đầu tiên đến chăm sóc sức khỏe lâu dài.
            </p>
            <ul className="mt-6 space-y-4">
              {points.map((p) => (
                <li key={p.title} className="flex gap-3">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-orange-100">
                    <p.icon className="size-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{p.title}</p>
                    <p className="mt-0.5 text-sm text-slate-500">{p.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
            <button onClick={() => booking.openBooking()}
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary/90">
              <Calendar className="size-4" />Đặt lịch tư vấn
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function FamilyTestimonialsSection() {
  return (
    <section className="bg-orange-50 px-4 py-16 sm:px-6 sm:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">Đánh giá</p>
          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">Gia đình nói gì về chúng tôi</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-3">
          {FAMILY_TESTIMONIALS.map((t) => (
            <div key={t.initials}
              className="relative flex flex-col rounded-2xl border border-orange-100 bg-white p-6 shadow-sm">
              <span className="absolute right-5 top-4 select-none font-serif text-5xl leading-none text-orange-100">"</span>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map((n) => <Star key={n} className="size-4 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-700">"{t.quote}"</p>
              <div className="mt-5 flex items-center gap-2.5 border-t border-orange-100 pt-4">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                  {t.initials.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{t.initials}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FamilyFaq({ booking }: { booking: BookingProvider }) {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return (
    <section className="bg-white px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-5xl">
        {/* Desktop: FAQ trái — CTA card phải */}
        <div className="grid gap-6 lg:grid-cols-[3fr_2fr] lg:items-start">
          {/* Left: accordion */}
          <div>
            <div className="mb-5 flex items-center gap-2">
              <HelpCircle className="size-5 text-primary" />
              <h2 className="text-base font-bold text-slate-900">Câu hỏi thường gặp</h2>
            </div>
            <div className="space-y-2">
              {FAMILY_FAQ.map((item, i) => {
                const isOpen = openIdx === i;
                return (
                  <div key={item.q} className="overflow-hidden rounded-2xl border border-orange-100 bg-orange-50/40">
                    <button onClick={() => setOpenIdx(isOpen ? null : i)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left">
                      <span className="text-sm font-semibold text-slate-800">{item.q}</span>
                      <ChevronDown className={cn("size-4 shrink-0 text-slate-500 transition-transform", isOpen && "rotate-180")} />
                    </button>
                    {isOpen && <p className="px-4 pb-4 text-sm leading-relaxed text-slate-600">{item.a}</p>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: CTA card */}
          <div className="rounded-2xl border border-orange-100 bg-orange-50 p-6">
            <p className="text-lg font-bold text-slate-900">Còn thắc mắc?</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Đội ngũ tư vấn của chúng tôi sẵn sàng hỗ trợ bạn chọn dịch vụ phù hợp nhất.
            </p>
            <Button
              onClick={() => booking.openBooking()}
              className="mt-5 w-full rounded-full bg-primary text-white hover:bg-primary/90">
              <Calendar className="mr-2 size-4" />Đặt lịch tư vấn
            </Button>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-white p-3 text-center">
                <p className="text-base font-bold text-slate-900">T2 – T7</p>
                <p className="text-xs text-slate-500">07:00 – 19:00</p>
              </div>
              <div className="rounded-xl bg-white p-3 text-center">
                <p className="text-base font-bold text-slate-900">Chủ nhật</p>
                <p className="text-xs text-slate-500">07:00 – 12:00</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FamilyContact({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const branches = payload.data.branches ?? [];
  if (!branches.length) return null;
  return (
    <section id="sec-contact" className="bg-orange-50 px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-5xl">
        {/* Desktop: địa chỉ trái — booking CTA phải */}
        <div className="grid gap-6 sm:grid-cols-[3fr_2fr] sm:items-start">
          {/* Left: branches */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">Liên hệ</p>
            <h2 className="mt-0.5 text-lg font-bold text-slate-900">Tìm chúng tôi</h2>
            <div className="mt-4 space-y-3">
              {branches.map((b) => (
                <div key={b.id} className="rounded-2xl border border-orange-100 bg-white p-4">
                  <h3 className="font-semibold text-slate-900">{b.name}</h3>
                  {b.address && (
                    <p className="mt-1.5 flex items-start gap-2 text-sm text-slate-600">
                      <MapPin className="mt-0.5 size-4 shrink-0 text-primary" />{b.address}
                    </p>
                  )}
                  {b.phone && (
                    <a href={`tel:${b.phone}`} className="mt-1 flex items-center gap-2 text-sm font-medium text-primary hover:underline">
                      <Phone className="size-4 shrink-0" />{b.phone}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: quick booking CTA */}
          <div className="rounded-2xl border border-orange-100 bg-white p-5">
            <p className="font-bold text-slate-900">Đặt lịch ngay</p>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
              Chọn bác sĩ, chuyên khoa và khung giờ phù hợp — nhận xác nhận ngay lập tức.
            </p>
            <Button
              onClick={() => booking.openBooking()}
              className="mt-4 w-full rounded-full bg-primary text-white hover:bg-primary/90">
              <Calendar className="mr-2 size-4" />Đặt lịch khám
            </Button>
            <div className="mt-4 flex items-start gap-2 text-xs text-slate-500">
              <Clock className="mt-0.5 size-3.5 shrink-0 text-slate-400" />
              <span>Thứ 2 – Thứ 7: 07:00–19:00 · Chủ nhật: 07:00–12:00</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FamilyFinalCta({ booking }: { booking: BookingProvider }) {
  return (
    <section className="relative overflow-hidden px-4 py-16 sm:px-6 sm:py-20">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-400 via-rose-400 to-pink-500" />
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -right-20 -top-20 size-72 rounded-full bg-white/10" />
        <div className="absolute -bottom-16 left-1/4 size-56 rounded-full bg-white/10" />
      </div>
      <div className="relative z-10 mx-auto max-w-2xl text-center">
        <Heart className="mx-auto mb-4 size-10 text-white/80" />
        <h2 className="text-2xl font-bold text-white sm:text-3xl">
          Sức khỏe gia đình — bắt đầu từ hôm nay
        </h2>
        <p className="mx-auto mt-4 max-w-md text-white/80">
          Đặt lịch ngay hôm nay và để đội ngũ bác sĩ tận tâm đồng hành cùng sức khỏe cả gia đình bạn.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button onClick={() => booking.openBooking()}
            className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-bold text-orange-700 shadow-lg transition-colors hover:bg-orange-50">
            <Calendar className="size-4" />Đặt lịch khám ngay
          </button>
          <button onClick={() => jump("sec-doctors")}
            className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/15 px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/25">
            Xem đội ngũ bác sĩ <ArrowRight className="size-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

function FamilyFooter({ payload }: { payload: SitePayload }) {
  const b = payload.data.branches?.[0];
  const specialties = payload.data.specialties ?? [];
  const services = payload.data.services ?? [];
  return (
    <footer className="bg-stone-800 px-4 py-8 sm:px-6">
      <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {/* Brand */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2.5">
            <span className="flex size-9 items-center justify-center rounded-2xl bg-white text-sm font-bold text-orange-700">
              {payload.clinicName.charAt(0).toUpperCase()}
            </span>
            <strong className="text-lg text-white">{payload.clinicName}</strong>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-white/55">
            Phòng khám gia đình — chăm sóc sức khoẻ cả nhà với đội ngũ bác sĩ tận tâm, giá minh bạch.
          </p>
          {b?.phone && (
            <a href={`tel:${b.phone}`} className="mt-3 flex items-center gap-2 text-xs text-white/65 hover:text-white transition-colors">
              <Phone className="size-3.5 shrink-0" />{b.phone}
            </a>
          )}
          {b?.address && (
            <p className="mt-1 flex items-start gap-2 text-xs text-white/55">
              <MapPin className="mt-0.5 size-3.5 shrink-0" />{b.address}
            </p>
          )}
        </div>
        {/* Specialties */}
        {specialties.length > 0 && (
          <div>
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-white/50">Chuyên khoa</h4>
            <ul className="space-y-2">
              {specialties.slice(0, 6).map((s) => (
                <li key={s.id} className="text-xs text-white/55 transition-colors hover:text-white cursor-default">{s.name}</li>
              ))}
            </ul>
          </div>
        )}
        {/* Services */}
        {services.length > 0 && (
          <div>
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-white/50">Dịch vụ</h4>
            <ul className="space-y-2">
              {services.slice(0, 6).map((s) => (
                <li key={s.id} className="text-xs text-white/55 line-clamp-1 transition-colors hover:text-white cursor-default">{s.name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="mx-auto mt-7 max-w-5xl border-t border-white/10 pt-5 text-center text-xs text-white/30">
        © {new Date().getFullYear()} {payload.clinicName} · Website tạo bởi Addy HIS
      </div>
    </footer>
  );
}

function FamilyClinicSite({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const raw = payload.data;
  const isDemo = payload.slug?.toUpperCase() === "DEMO";
  const d = {
    ...raw,
    specialties: isDemo ? FAMILY_DEMO_SPECIALTIES : (raw.specialties?.length ? raw.specialties : FAMILY_DEMO_SPECIALTIES),
    services:    isDemo ? FAMILY_DEMO_SERVICES    : (raw.services?.length    ? raw.services    : FAMILY_DEMO_SERVICES),
  };
  return (
    <div style={FAMILY_CLINIC_ACCENT}>
      <FamilyTopBar payload={payload} />
      <FamilyNav payload={payload} booking={booking} />
      <main>
        <FamilyHero payload={payload} booking={booking} />
        <FamilyTrustStrip />
        <FamilyFeaturedCare booking={booking} />
        <FamilyBookingSteps booking={booking} />
        <FamilyServices items={d.services ?? []} booking={booking} />
        <FamilyDoctors items={d.doctors ?? []} booking={booking} />
        <FamilyAbout payload={payload} booking={booking} />
        <FamilyTestimonialsSection />
        <FamilyFaq booking={booking} />
        <FamilyContact payload={payload} booking={booking} />
        <FamilyFinalCta booking={booking} />
      </main>
      <FamilyFooter payload={payload} />
    </div>
  );
}

// ─── Diagnostic theme ───────────────────────────────────────────────────────
// Clinical lab — dark dramatic hero, services/packages FIRST with bento grid,
// process timeline, data-forward.

const DIAGNOSTIC_CATEGORY_LABELS: Record<string, string> = {
  "kham benh": "Khám bệnh",
  "xet nghiem": "Xét nghiệm",
  "chan doan hinh anh": "Chẩn đoán hình ảnh",
  "kham chuyen khoa": "Chuyên khoa",
};

function prettyCategory(raw: string): string {
  const key = raw.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "").trim();
  return DIAGNOSTIC_CATEGORY_LABELS[key] ?? raw;
}

function DiagnosticTopBar() {
  return (
    <div className="bg-teal-900 px-4 py-2 text-white">
      <div className="mx-auto flex max-w-6xl justify-between text-xs">
        <span>📍 Kết quả trong ngày với hầu hết xét nghiệm</span>
        <span className="hidden sm:block">Thứ 2–7: 07:00–19:00 · CN: 07:00–12:00</span>
      </div>
    </div>
  );
}

function DiagnosticHero({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const [q, setQ] = useState("");
  const d = payload.data;
  const handleSearch = () => {
    if ((d.services?.length ?? 0) > 0) return jump("sec-services");
    if ((d.doctors?.length ?? 0) > 0)  return jump("sec-doctors");
  };
  const quickSvcs = (d.services ?? []).slice(0, 3);
  return (
    <section className="relative min-h-[360px] overflow-hidden px-4 py-8 sm:px-6">
      <img src={DIAGNOSTIC_HERO_PHOTO} alt="" className="absolute inset-0 size-full object-cover" />
      <div className="absolute inset-0 bg-teal-950/85" />
      <div className="relative z-10 mx-auto max-w-4xl text-center">
        <h1 className="text-3xl font-bold text-white sm:text-4xl">{payload.clinicName}</h1>
        <p className="mx-auto mt-3 max-w-xl text-sm text-white/80 sm:text-base">
          Xét nghiệm, chẩn đoán hình ảnh và khám sức khoẻ — đặt lịch nhanh, kết quả rõ ràng.
        </p>
        <div className="mx-auto mt-7 max-w-xl">
          <div className="flex items-center rounded-full bg-white px-5 py-2 shadow-2xl">
            <Search className="size-5 shrink-0 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
              placeholder="Tìm gói xét nghiệm, dịch vụ, bác sĩ..."
              className="w-full border-0 bg-transparent py-2 pl-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
            />
          </div>
          {quickSvcs.length > 0 && (
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <span className="text-xs text-white/60">Tìm nhanh:</span>
              {quickSvcs.map((s) => (
                <button key={s.id} onClick={() => booking.bookService(s.id)}
                  className="rounded-full border border-white/30 px-3 py-0.5 text-xs text-white/80 hover:border-white hover:text-white">
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button className="rounded-full bg-white text-teal-900 hover:bg-white/90" onClick={() => booking.openBooking()}>
            <Calendar className="mr-2 size-4" />Đặt lịch hẹn
          </Button>
          <Button variant="outline" className="rounded-full border-white/40 bg-white/10 text-white hover:bg-white/20" onClick={() => jump("sec-services")}>
            <Package className="mr-2 size-4" />Xem dịch vụ
          </Button>
        </div>
      </div>
    </section>
  );
}

function DiagnosticServiceCard({ svc, idx, booking }: {
  svc: NonNullable<SitePayload["data"]["services"]>[number];
  idx: number;
  booking: BookingProvider;
}) {
  const Icon = SVC_THEMES[idx % SVC_THEMES.length].icon;
  const hasPrice = svc.priceVnd != null;
  return (
    <Card className="flex h-full flex-col overflow-hidden rounded-xl border-border shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
      <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
        <img src={SVC_PHOTOS[idx % SVC_PHOTOS.length]} alt="" loading="lazy" className="size-full object-cover" />
      </div>
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-start gap-2">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Icon className="size-3.5 text-primary" />
          </span>
          <h3 className="min-h-[2.5em] text-sm font-semibold leading-snug text-foreground line-clamp-2">{svc.name}</h3>
        </div>
        {svc.description && (
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground line-clamp-2">{svc.description}</p>
        )}
        <div className="mt-3 flex flex-1 items-end justify-between gap-2 border-t border-border pt-3">
          <span className="text-sm font-bold text-primary">
            {hasPrice ? (svc.priceVnd === 0 ? "Miễn phí" : `Từ ${fmtPrice(svc.priceVnd)}`) : ""}
          </span>
          <button onClick={() => booking.bookService(svc.id)}
            className="flex shrink-0 items-center gap-1 text-sm font-medium text-primary hover:underline">
            Đặt lịch <ArrowRight className="size-3.5" />
          </button>
        </div>
      </div>
    </Card>
  );
}

function DiagnosticPackagesGrid({ items, booking }: {
  items: NonNullable<SitePayload["data"]["services"]>;
  booking: BookingProvider;
}) {
  const categories = useMemo(
    () => Array.from(new Set(items.map((s) => s.category).filter((c): c is string => !!c))),
    [items],
  );
  const [active, setActive] = useState<string | null>(null);
  const shown = active ? items.filter((s) => s.category === active) : items;

  if (!items.length) return null;
  return (
    <section id="sec-services" className="bg-muted px-4 pb-8 pt-12 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-base font-bold tracking-tight text-primary-foreground shadow-md">
            <Package className="size-5" />Dịch vụ khám &amp; xét nghiệm
          </span>
          <p className="mt-3 text-sm text-muted-foreground">Chọn dịch vụ phù hợp và đặt lịch khám ngay</p>
        </div>

        {categories.length > 1 && (
          <div className="mb-5 flex flex-wrap justify-center gap-2">
            <button onClick={() => setActive(null)}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                active === null ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-primary/10 hover:text-primary",
              )}>
              Tất cả
            </button>
            {categories.map((c) => (
              <button key={c} onClick={() => setActive(c)}
                className={cn(
                  "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                  active === c ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-primary/10 hover:text-primary",
                )}>
                {prettyCategory(c)}
              </button>
            ))}
          </div>
        )}

        {/* One row, horizontal scroll — scales to any service count without
            growing the page vertically (no pagination state needed). */}
        <CarouselRow dots>
          {shown.map((s, i) => (
            <div key={s.id} className="snap-start shrink-0 w-[78vw] sm:w-[260px]">
              <DiagnosticServiceCard svc={s} idx={i} booking={booking} />
            </div>
          ))}
        </CarouselRow>
      </div>
    </section>
  );
}

// "specialties":"grid" — departments as icon+name tiles, denser than the
// inline-chip treatment family-clinic uses.
function DiagnosticSpecialtyGrid({ items, booking }: {
  items: NonNullable<SitePayload["data"]["specialties"]>;
  booking: BookingProvider;
}) {
  if (!items.length) return null;
  return (
    <section id="sec-specialties" className="px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <h2 className="text-center text-lg font-bold text-foreground sm:text-xl">Chuyên khoa khám</h2>
        <p className="mt-1 text-center text-sm text-muted-foreground">Đội ngũ chuyên môn theo từng lĩnh vực</p>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((s, i) => {
            const c = SP_COLORS[i % SP_COLORS.length];
            const Icon = c.icon;
            return (
              <button key={s.id} onClick={() => booking.openBooking()}
                className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                <span className={cn("flex size-11 shrink-0 items-center justify-center rounded-full", c.bg)}>
                  <Icon className={cn("size-5", c.text)} />
                </span>
                <span className="text-sm font-semibold text-foreground">{s.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function DiagnosticDoctorsGrid({ items, booking }: {
  items: NonNullable<SitePayload["data"]["doctors"]>;
  booking: BookingProvider;
}) {
  if (!items.length) return null;
  return (
    <section id="sec-doctors" className="bg-muted px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-4 text-center">
          <h2 className="text-lg font-bold text-foreground sm:text-xl">Đội ngũ bác sĩ &amp; chuyên gia</h2>
          <p className="mt-1 text-sm text-muted-foreground">Đọc kết quả, tư vấn hướng điều trị rõ ràng</p>
        </div>
        <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-4">
          {items.slice(0, 8).map((doc, i) => (
            <DoctorCard key={doc.id} doc={doc} idx={i} booking={booking} />
          ))}
        </div>
      </div>
    </section>
  );
}

function DiagnosticStats({ data }: { data: SitePayload["data"] }) {
  const items = [
    { count: "Trong ngày",                           label: "Trả kết quả xét nghiệm thường quy" },
    { count: `${data.services?.length ?? 0}+`,       label: "Gói dịch vụ xét nghiệm" },
    { count: `${data.doctors?.length ?? 0}+`,        label: "Bác sĩ đọc kết quả" },
    { count: `${data.specialties?.length ?? 0}+`,    label: "Chuyên khoa" },
  ];
  return (
    <section className="bg-teal-900 px-4 py-8 sm:px-6">
      <div className="mx-auto grid max-w-5xl grid-cols-2 gap-6 sm:grid-cols-4">
        {items.map((s) => (
          <div key={s.label} className="text-center">
            <div className="text-2xl font-bold text-white">{s.count}</div>
            <div className="mt-1 text-xs text-teal-200">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

const DIAGNOSTIC_STEPS = [
  { icon: Calendar,   title: "Đặt lịch hẹn",       desc: "Chọn gói xét nghiệm hoặc dịch vụ, đặt lịch trực tuyến." },
  { icon: ScanLine,   title: "Lấy mẫu / chẩn đoán", desc: "Đến đúng giờ hẹn — quy trình lấy mẫu, chụp chiếu nhanh gọn." },
  { icon: FlaskConical, title: "Nhận kết quả",      desc: "Kết quả được xử lý và gửi đến bạn theo thời gian cam kết." },
  { icon: UserCheck,  title: "Bác sĩ tư vấn",       desc: "Bác sĩ đọc kết quả và tư vấn hướng theo dõi, điều trị." },
] as const;

function DiagnosticProcess() {
  return (
    <section className="relative overflow-hidden px-4 py-7 sm:px-6">
      <img src={DIAGNOSTIC_HERO_PHOTO} alt="" aria-hidden={true} className="absolute inset-0 size-full object-cover" style={{ filter: "blur(12px)", opacity: 0.06, transform: "scale(1.12)" }} />
      <div className="absolute inset-0 bg-white/90" />
      <div className="relative z-10 mx-auto max-w-5xl">
        <div className="mb-5 text-center">
          <h2 className="text-lg font-bold text-foreground sm:text-xl">Quy trình xét nghiệm</h2>
          <p className="mt-1 text-sm text-muted-foreground">Đơn giản, minh bạch từ đặt lịch đến khi có kết quả</p>
        </div>
        <div className="relative grid gap-6 sm:grid-cols-4">
          <div className="absolute top-7 hidden h-px w-full border-t-2 border-dashed border-border sm:block" />
          {DIAGNOSTIC_STEPS.map((s, i) => (
            <div key={s.title} className="relative flex flex-col items-center gap-2.5 text-center">
              <div className="relative z-10 flex size-14 items-center justify-center rounded-full border-4 border-background bg-primary/10">
                <s.icon className="size-6 text-primary" />
                <span className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                  {i + 1}
                </span>
              </div>
              <h3 className="text-sm font-semibold text-foreground">{s.title}</h3>
              <p className="text-xs leading-relaxed text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const DIAGNOSTIC_FAQ = [
  { q: "Cần nhịn ăn trước khi xét nghiệm không?", a: "Tuỳ gói xét nghiệm — một số gói (đường huyết, mỡ máu) cần nhịn ăn 8-12 giờ. Thông tin cụ thể hiển thị khi chọn dịch vụ." },
  { q: "Bao lâu thì có kết quả?", a: "Thời gian trả kết quả tuỳ loại xét nghiệm, thường trong ngày đến vài ngày làm việc." },
  { q: "Có thể xem kết quả trực tuyến không?", a: "Có — kết quả được gửi đến bạn qua các kênh liên hệ đã đăng ký khi đặt lịch." },
  { q: "Giá gói xét nghiệm được công khai ở đâu?", a: "Giá niêm yết hiển thị ngay trong mục dịch vụ trên trang này." },
] as const;

function DiagnosticFaq() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return (
    <section className="bg-muted px-4 py-7 sm:px-6">
      <div className="mx-auto max-w-3xl">
        <div className="mb-4 flex items-center justify-center gap-2 text-center">
          <HelpCircle className="size-5 text-primary" />
          <h2 className="text-lg font-bold text-foreground sm:text-xl">Câu hỏi thường gặp</h2>
        </div>
        <div className="space-y-2.5">
          {DIAGNOSTIC_FAQ.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={item.q} className="overflow-hidden rounded-xl border border-border bg-card">
                <button onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left">
                  <span className="text-sm font-semibold text-foreground">{item.q}</span>
                  <ChevronDown className={cn("size-4 shrink-0 text-muted-foreground transition-transform", isOpen && "rotate-180")} />
                </button>
                {isOpen && <p className="px-4 pb-4 text-sm leading-relaxed text-muted-foreground">{item.a}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// "contact":"map-split"
function DiagnosticContact({ payload }: { payload: SitePayload }) {
  const branches = payload.data.branches ?? [];
  if (!branches.length) return null;
  const single = branches.length === 1;
  return (
    <section id="sec-contact" className="px-4 py-7 sm:px-6">
      <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <h2 className="text-lg font-bold text-foreground sm:text-xl">Điểm lấy mẫu &amp; phòng khám</h2>
          <p className="mt-1 text-sm text-muted-foreground">Ghé thăm hoặc gọi trước để được hỗ trợ nhanh nhất</p>
          <div className="mt-4 space-y-3">
            {branches.map((b) => (
              <div key={b.id} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start gap-2.5">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <Building2 className="size-4 text-primary" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-foreground">{b.name}</h3>
                    {b.address && (
                      <p className="mt-1 flex items-start gap-1.5 text-sm text-muted-foreground">
                        <MapPin className="mt-0.5 size-3.5 shrink-0 text-primary" />{b.address}
                      </p>
                    )}
                    {b.phone && (
                      <a href={`tel:${b.phone}`} className="mt-1 flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                        <Phone className="size-3.5 shrink-0" />{b.phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative hidden overflow-hidden rounded-3xl border border-border bg-card lg:block">
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
            <span className="flex size-16 items-center justify-center rounded-full bg-primary/10 shadow-sm">
              <MapPin className="size-8 text-primary" />
            </span>
            <p className="text-sm font-medium text-foreground">
              {single ? branches[0].name : `${branches.length} điểm đang phục vụ`}
            </p>
            <p className="text-xs text-muted-foreground">
              {single ? "Ghé thăm hoặc đặt lịch trước khi đến" : "Chọn điểm gần bạn nhất để đặt lịch"}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DiagnosticFooter({ payload }: { payload: SitePayload }) {
  const branches = payload.data.branches ?? [];
  return (
    <footer className="bg-[#0d2b3a] px-4 py-7 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center gap-2.5">
          <span className="flex size-9 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
            {payload.clinicName.charAt(0).toUpperCase()}
          </span>
          <strong className="text-white">{payload.clinicName}</strong>
        </div>
        {branches.length > 0 && (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {branches.map((b) => (
              <div key={b.id} className="rounded-xl border border-white/10 p-4">
                <h4 className="text-sm font-semibold text-white">{b.name}</h4>
                {b.address && <p className="mt-1 text-xs text-white/60">{b.address}</p>}
                {b.phone && <p className="mt-1 text-xs text-primary-foreground/90">{b.phone}</p>}
              </div>
            ))}
          </div>
        )}
        <div className="mt-8 border-t border-white/10 pt-6 text-center text-xs text-white/50">
          © {new Date().getFullYear()} {payload.clinicName} · Website tạo bởi Addy HIS
        </div>
      </div>
    </footer>
  );
}

function DiagnosticSite({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const raw = payload.data;
  const isDemo = payload.slug?.toUpperCase() === "DEMO";
  const d = {
    ...raw,
    specialties: isDemo ? DIAGNOSTIC_DEMO_SPECIALTIES : (raw.specialties?.length ? raw.specialties : DIAGNOSTIC_DEMO_SPECIALTIES),
    services:    isDemo ? DIAGNOSTIC_DEMO_SERVICES    : (raw.services?.length    ? raw.services    : DIAGNOSTIC_DEMO_SERVICES),
  };
  return (
    <div style={DIAGNOSTIC_ACCENT}>
      <DiagnosticTopBar />
      <SiteNav payload={payload} booking={booking} />
      <main>
        <DiagnosticHero payload={payload} booking={booking} />
        {d.services?.length    ? <DiagnosticPackagesGrid items={d.services} booking={booking} />    : null}
        <DiagnosticProcess />
        {d.specialties?.length ? <DiagnosticSpecialtyGrid items={d.specialties} booking={booking} /> : null}
        {d.doctors?.length     ? <DiagnosticDoctorsGrid items={d.doctors} booking={booking} />       : null}
        <DiagnosticStats data={d} />
        <DiagnosticFaq />
        <DiagnosticContact payload={payload} />
        <CtaBanner booking={booking} />
      </main>
      <DiagnosticFooter payload={payload} />
    </div>
  );
}

// ─── Dental / Beauty theme ──────────────────────────────────────────────────
// Luxury aesthetic — full-screen dark dramatic hero, magazine service grid,
// large portrait doctors, elegant throughout.

const DENTAL_TESTIMONIALS = [
  { initials: "KH", role: "Khách hàng niềng răng", quote: "Bác sĩ tư vấn kỹ, theo dõi sát từng giai đoạn — kết quả đúng như mong đợi." },
  { initials: "TM", role: "Khách hàng thẩm mỹ da", quote: "Không gian sạch sẽ, quy trình rõ ràng, không có chi phí phát sinh bất ngờ." },
  { initials: "PH", role: "Phụ huynh đưa con khám", quote: "Bác sĩ nhẹ nhàng với trẻ nhỏ, đặt lịch và nhắc lịch tái khám rất tiện." },
] as const;

const DENTAL_FAQ = [
  { q: "Điều trị có đau không?", a: "Hầu hết dịch vụ đều được gây tê/vô cảm phù hợp — bác sĩ sẽ tư vấn cụ thể theo tình trạng của bạn trước khi thực hiện." },
  { q: "Chi phí có công khai trước không?", a: "Có — giá dịch vụ hiển thị ngay trong mục Dịch vụ, không phát sinh chi phí ngoài dự kiến." },
  { q: "Cần tái khám bao nhiêu lần?", a: "Tuỳ liệu trình — bác sĩ sẽ lên lịch tái khám cụ thể và nhắc lịch để bạn theo dõi kết quả." },
] as const;

function DentalTopBar({ payload }: { payload: SitePayload }) {
  const phone = payload.data.branches?.[0]?.phone;
  return (
    <div className="bg-rose-600 px-4 py-2 text-center text-xs text-white">
      <span className="font-medium">✨ Tư vấn miễn phí — đặt lịch ngay để nhận ưu đãi đặc biệt</span>
      {phone && (
        <span className="ml-2 hidden sm:inline">
          · <a href={`tel:${phone}`} className="font-bold underline">{phone}</a>
        </span>
      )}
    </div>
  );
}

function DentalHero({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const d = payload.data;
  return (
    <section className="relative h-[90vh] overflow-hidden">
      {/* Nav inside hero — transparent, position absolute */}
      <div className="absolute inset-x-0 top-0 z-20 px-4 py-4 sm:px-6">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <a href={`/site/${payload.slug}`} className="flex items-center gap-2 font-bold text-white">
            <span className="flex size-9 items-center justify-center rounded-xl bg-white/20 text-sm font-bold text-white backdrop-blur">
              {payload.clinicName.charAt(0).toUpperCase()}
            </span>
            <span className="hidden text-lg sm:block">{payload.clinicName}</span>
          </a>
          <div className="flex items-center gap-3">
            <button onClick={() => jump("sec-services")} className="hidden text-sm text-white/80 hover:text-white lg:block">Dịch vụ</button>
            <button onClick={() => jump("sec-doctors")} className="hidden text-sm text-white/80 hover:text-white lg:block">Bác sĩ</button>
            <Button onClick={() => booking.openBooking()} className="rounded-full bg-rose-500 text-white hover:bg-rose-600">
              Đặt lịch
            </Button>
          </div>
        </div>
      </div>
      {/* Full-bleed photo */}
      <img src={PREMIUM_HERO_PHOTO} alt="" className="size-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10" />
      {/* Content pinned to bottom */}
      <div className="absolute inset-x-0 bottom-0 px-4 pb-14 sm:px-6">
        <div className="mx-auto max-w-4xl">
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-rose-300">Nha khoa &amp; Thẩm mỹ</p>
          <h1 className="text-5xl font-bold leading-tight text-white">
            Vẻ đẹp tự nhiên,<br />nụ cười tự tin
          </h1>
          <p className="mt-4 max-w-lg text-white/80">
            Nha khoa, thẩm mỹ, chăm sóc da — đội ngũ chuyên môn cao, kết quả thật, minh bạch.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button size="lg" className="rounded-full bg-rose-500 text-white hover:bg-rose-600 shadow-lg" onClick={() => booking.openBooking()}>
              <Calendar className="mr-2 size-4" />Đặt lịch tư vấn miễn phí
            </Button>
            {(d.services?.length ?? 0) > 0 && (
              <Button size="lg" variant="outline" className="rounded-full border-white/50 bg-white/10 text-white hover:bg-white/20" onClick={() => jump("sec-services")}>
                Xem dịch vụ
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function DentalServicesGrid({ items, booking }: {
  items: NonNullable<SitePayload["data"]["services"]>;
  booking: BookingProvider;
}) {
  if (!items.length) return null;
  const shown = items.slice(0, 6);
  const big = shown.slice(0, 2);
  const small = shown.slice(2);
  return (
    <section id="sec-services" className="bg-rose-50 px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <h2 className="mb-5 text-center text-2xl font-bold text-slate-900">Dịch vụ nổi bật</h2>
        {/* Row 1: 2 large cards */}
        {big.length > 0 && (
          <div className="mb-4 grid grid-cols-2 gap-4">
            {big.map((svc, i) => (
              <div key={svc.id}
                className="group relative h-72 overflow-hidden rounded-2xl cursor-pointer"
                onClick={() => booking.bookService(svc.id)}>
                <img src={SVC_PHOTOS[i % SVC_PHOTOS.length]} alt="" loading="lazy"
                  className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <h3 className="font-bold text-white line-clamp-1">{svc.name}</h3>
                  <p className="text-sm text-rose-300">{fmtPrice(svc.priceVnd)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Row 2: 3-4 smaller cards */}
        {small.length > 0 && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {small.map((svc, i) => (
              <div key={svc.id}
                className="group relative h-48 overflow-hidden rounded-2xl cursor-pointer"
                onClick={() => booking.bookService(svc.id)}>
                <img src={SVC_PHOTOS[(i + 2) % SVC_PHOTOS.length]} alt="" loading="lazy"
                  className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-3">
                  <h3 className="text-sm font-bold text-white line-clamp-1">{svc.name}</h3>
                  <p className="text-xs text-rose-300">{fmtPrice(svc.priceVnd)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function DentalGallery() {
  return (
    <section className="bg-rose-100 px-4 py-7 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <h2 className="mb-4 text-center text-xl font-bold text-slate-900">Không gian phòng khám</h2>
        <div className="grid h-80 grid-cols-3 gap-3">
          {/* Left: 1 tall */}
          <div className="group relative col-span-1 row-span-2 overflow-hidden rounded-2xl">
            <img src={FACILITY_PHOTOS[0].url} alt="" loading="lazy"
              className="size-full object-cover transition-transform duration-500 group-hover:scale-105" />
          </div>
          {/* Right: 3 in 2-col */}
          {FACILITY_PHOTOS.slice(1).map((f) => (
            <div key={f.label} className="group relative overflow-hidden rounded-2xl">
              <img src={f.url} alt="" loading="lazy"
                className="size-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent p-2">
                <p className="text-xs font-medium text-white">{f.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DentalDoctorsCarousel({ items, booking }: {
  items: NonNullable<SitePayload["data"]["doctors"]>;
  booking: BookingProvider;
}) {
  if (!items.length) return null;
  return (
    <section id="sec-doctors" className="bg-rose-50 px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <h2 className="mb-5 text-center text-2xl font-bold text-slate-900">Đội ngũ chuyên gia</h2>
        <CarouselRow dots>
          {items.map((doc, i) => (
            <div key={doc.id} className="snap-start shrink-0 w-72">
              <button onClick={() => booking.bookDoctor(doc.id)} className="w-full text-left overflow-hidden rounded-2xl border border-rose-200 shadow-sm hover:shadow-md transition-shadow">
                <img src={DOCTOR_PHOTOS[i % DOCTOR_PHOTOS.length]} alt=""
                  className="h-96 w-full object-cover object-top" style={{ height: "24rem" }} />
                <div className="bg-rose-100 p-4">
                  <h3 className="font-bold text-slate-900">{doc.name}</h3>
                  <p className="text-sm text-rose-400">{doc.title ?? "Bác sĩ chuyên khoa"}</p>
                  <button className="mt-3 w-full rounded-full bg-rose-500 py-2 text-sm font-medium text-white hover:bg-rose-600">
                    Đặt lịch
                  </button>
                </div>
              </button>
            </div>
          ))}
        </CarouselRow>
      </div>
    </section>
  );
}

function DentalTestimonialsSection() {
  const [idx, setIdx] = useState(0);
  const total = DENTAL_TESTIMONIALS.length;
  const t = DENTAL_TESTIMONIALS[idx];
  function go(dir: 1 | -1) { setIdx((i) => (i + dir + total) % total); }
  return (
    <section className="bg-slate-900 px-4 py-7 sm:px-6">
      <div className="mx-auto max-w-2xl text-center">
        <div className="flex justify-center mb-4">
          {[1,2,3,4,5].map((n) => <Star key={n} className="size-5 fill-amber-400 text-amber-400" />)}
        </div>
        <p className="text-2xl font-light italic text-white">"{t.quote}"</p>
        <p className="mt-4 text-sm text-white/60">{t.role}</p>
        <div className="mt-6 flex items-center justify-center gap-4">
          <button type="button" aria-label="Trước" onClick={() => go(-1)}
            className="flex size-9 items-center justify-center rounded-full border border-white/30 text-white hover:bg-white/10">
            <ChevronLeft className="size-4" />
          </button>
          <div className="flex gap-2">
            {DENTAL_TESTIMONIALS.map((_, i) => (
              <button key={i} type="button" aria-label={`Đánh giá ${i + 1}`} onClick={() => setIdx(i)}
                className={cn("size-2 rounded-full transition-all", i === idx ? "bg-white" : "bg-white/30")} />
            ))}
          </div>
          <button type="button" aria-label="Tiếp" onClick={() => go(1)}
            className="flex size-9 items-center justify-center rounded-full border border-white/30 text-white hover:bg-white/10">
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

function DentalFaq() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return (
    <section className="bg-rose-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-2xl">
        <h2 className="mb-4 text-center text-xl font-bold text-slate-900">Câu hỏi thường gặp</h2>
        <div className="space-y-2">
          {DENTAL_FAQ.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={item.q} className="overflow-hidden rounded-xl border border-rose-200 bg-rose-100">
                <button onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left">
                  <span className="text-sm font-semibold text-slate-800">{item.q}</span>
                  <ChevronDown className={cn("size-4 shrink-0 text-slate-400 transition-transform", isOpen && "rotate-180")} />
                </button>
                {isOpen && <p className="px-5 pb-4 text-sm leading-relaxed text-slate-600">{item.a}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function DentalStickyBanner({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const phone = payload.data.branches?.[0]?.phone;
  return (
    <div className="sticky top-16 z-30 px-4 sm:px-6">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-rose-500 to-rose-600 px-5 py-3 shadow-lg">
        <p className="text-sm font-semibold text-white">Sẵn sàng cho diện mạo mới?</p>
        <div className="flex shrink-0 gap-2">
          {phone && (
            <a href={`tel:${phone}`} className="hidden rounded-full border border-white/40 bg-white/10 px-4 py-1.5 text-xs font-medium text-white hover:bg-white/20 sm:block">
              <Phone className="mr-1.5 inline size-3.5" />{phone}
            </a>
          )}
          <button onClick={() => booking.openBooking()}
            className="rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-rose-600 hover:bg-white/90">
            Đặt lịch ngay
          </button>
        </div>
      </div>
    </div>
  );
}

function DentalContact({ payload }: { payload: SitePayload }) {
  const branches = payload.data.branches ?? [];
  if (!branches.length) return null;
  return (
    <section id="sec-contact" className="bg-slate-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="mb-5 text-xl font-bold text-slate-900">Hệ thống chi nhánh</h2>
        <div className="space-y-3">
          {branches.map((b) => (
            <div key={b.id} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-rose-50">
                <Building2 className="size-5 text-rose-500" />
              </span>
              <div>
                <h3 className="font-semibold text-slate-900">{b.name}</h3>
                {b.address && <p className="mt-1 text-sm text-slate-600">{b.address}</p>}
                {b.phone && (
                  <a href={`tel:${b.phone}`} className="mt-1 flex items-center gap-1.5 text-sm font-medium text-rose-500 hover:underline">
                    <Phone className="size-3.5" />{b.phone}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DentalFooter({ payload }: { payload: SitePayload }) {
  const b = payload.data.branches?.[0];
  return (
    <footer className="bg-slate-900 px-4 py-6 sm:px-6 text-center">
      <span className="mx-auto flex size-10 items-center justify-center rounded-full bg-rose-500 text-sm font-bold text-white">
        {payload.clinicName.charAt(0).toUpperCase()}
      </span>
      <p className="mt-2 font-semibold text-white">{payload.clinicName}</p>
      {b?.address && <p className="mt-1 text-xs text-slate-400">{b.address}</p>}
      {b?.phone && <p className="mt-1 text-xs text-slate-400">{b.phone}</p>}
      <p className="mt-5 text-xs text-slate-600">© {new Date().getFullYear()} {payload.clinicName} · Website tạo bởi Addy HIS</p>
    </footer>
  );
}

function DentalBeautySite({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const raw = payload.data;
  const isDemo = payload.slug?.toUpperCase() === "DEMO";
  const d = {
    ...raw,
    specialties: isDemo ? DENTAL_DEMO_SPECIALTIES : (raw.specialties?.length ? raw.specialties : DENTAL_DEMO_SPECIALTIES),
    services:    isDemo ? DENTAL_DEMO_SERVICES    : (raw.services?.length    ? raw.services    : DENTAL_DEMO_SERVICES),
  };
  return (
    <div style={DENTAL_BEAUTY_ACCENT}>
      <DentalTopBar payload={payload} />
      <main>
        <DentalHero payload={payload} booking={booking} />
        <DentalStickyBanner payload={payload} booking={booking} />
        {d.services?.length ? <DentalServicesGrid items={d.services} booking={booking} /> : null}
        <DentalGallery />
        {d.doctors?.length  ? <DentalDoctorsCarousel items={d.doctors} booking={booking} /> : null}
        <DentalTestimonialsSection />
        <DentalFaq />
        {d.branches?.length ? <DentalContact payload={payload} /> : null}
      </main>
      <DentalFooter payload={payload} />
    </div>
  );
}

// ─── Specialist theme (default) ────────────────────────────────────────────
// Doctor directory — dark navy hero, doctors-first as formal directory,
// numbered service list.

function SpecialistSite({ payload, booking }: { payload: SitePayload; booking: BookingProvider }) {
  const raw = payload.data;
  const isDemo = payload.slug?.toUpperCase() === "DEMO";
  const d = {
    ...raw,
    specialties: isDemo ? SPECIALIST_DEMO_SPECIALTIES : (raw.specialties?.length ? raw.specialties : SPECIALIST_DEMO_SPECIALTIES),
    services:    isDemo ? SPECIALIST_DEMO_SERVICES    : (raw.services?.length    ? raw.services    : SPECIALIST_DEMO_SERVICES),
  };
  return (
    <div style={SPECIALIST_ACCENT}>
      <SpecialistTopBar payload={payload} />
      <SpecialistNav payload={payload} booking={booking} />
      <main>
        <SpecialistHero payload={payload} booking={booking} />
        <SpecialistTrustStrip />
        <SpecialistDoctorGrid items={d.doctors ?? []} booking={booking} />
        <SpecialistStatsBand data={d} />
        <SpecialistSpecialtyGrid items={d.specialties ?? []} booking={booking} />
        <SpecialistServiceCards items={d.services ?? []} booking={booking} />
        <SpecialistTrustSection booking={booking} />
        <SpecialistAbout payload={payload} booking={booking} />
        <SpecialistTestimonials />
        <SpecialistFaq booking={booking} />
        <SpecialistContact payload={payload} booking={booking} />
        <SpecialistFinalCta booking={booking} />
      </main>
      <SpecialistFooter payload={payload} />
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export function PublicSitePage() {
  const { slug = "" } = useParams();
  const [searchParams] = useSearchParams();
  const [intent, setIntent] = useState<BookingIntent | null>(null);
  const booking = useMemo(() => createDemoBookingProvider(setIntent), []);

  // This page is a standalone template with its own fixed light palette — it
  // never follows the admin dashboard's dark/light preference. Restore
  // whatever <html> had on the way out so leaving this page back into the
  // admin app doesn't strand it in light mode.
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    root.classList.remove("dark");
    return () => {
      if (hadDark) root.classList.add("dark");
    };
  }, []);

  const site = useQuery({
    queryKey: ["public-site", slug],
    queryFn: async () =>
      (await publicClient.get<SitePayload>(`/v1/public/site/${slug}`)).data,
    enabled: slug.length > 0,
    retry: false,
  });

  if (site.isLoading) return <PageSkeleton />;
  if (site.isError || !site.data) return <NotFoundPage />;

  const { data: payload } = site;
  const d = payload.data;

  // configuration.themeId is set when the site is published and is the authoritative
  // source; the root themeId reflects the draft state which can drift from the
  // published version's actual theme.
  const themeId =
    searchParams.get("theme") ??
    (payload.configuration as { themeId?: string } | undefined)?.themeId ??
    payload.themeId;

  return (
    <div className="min-h-screen bg-background pb-20 sm:pb-0">
      {themeId === "premium-medical" ? <PremiumMedicalSite payload={payload} booking={booking} />
        : themeId === "family-clinic" ? <FamilyClinicSite payload={payload} booking={booking} />
        : themeId === "diagnostic" ? <DiagnosticSite payload={payload} booking={booking} />
        : themeId === "dental-beauty" ? <DentalBeautySite payload={payload} booking={booking} />
        : <SpecialistSite payload={payload} booking={booking} />}

      {/* Mobile sticky bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 flex gap-2 border-t border-border bg-card p-3 shadow-xl sm:hidden">
        {d.branches?.[0]?.phone && (
          <a href={`tel:${d.branches[0].phone}`}
            className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-border bg-background text-sm font-medium text-foreground">
            <Phone className="size-4 text-primary" />Gọi ngay
          </a>
        )}
        <Button className="h-11 flex-1 rounded-lg" onClick={() => booking.openBooking()}>
          <Calendar className="mr-1.5 size-4" />Đặt lịch khám
        </Button>
      </div>

      {/* Booking intent toast */}
      {intent && (
        <div role="status"
          className="fixed inset-x-4 bottom-24 z-50 mx-auto flex max-w-sm items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm shadow-2xl sm:inset-x-auto sm:right-6 sm:bottom-6">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="size-5 shrink-0 text-green-500" />
            <span className="text-foreground">
              Đã ghi nhận yêu cầu đặt lịch
              {intent.kind === "service" && " — dịch vụ"}
              {intent.kind === "doctor"  && " — bác sĩ"}
            </span>
          </div>
          <button onClick={() => setIntent(null)} aria-label="Đóng"
            className="rounded p-1 text-muted-foreground hover:bg-muted">
            <X className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
}
